import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";


// ─── i18n ─────────────────────────────────────────────────────────────────────

function detectLang() {
  const saved = localStorage.getItem('hkia-lang');
  if (saved) return saved;
  const lang = navigator.language || navigator.userLanguage || 'es';
  return lang.startsWith('es') ? 'es' : 'en';
}

const T = {
  es: {
    appSubtitle: "Coleccionador de Flores 🌷",
    totalProgress: "Progreso total",
    colors: "colores",
    logout: "Cerrar sesión",
    tabReg: "🌿 Regionales",
    tabEvt: "🎉 Eventos",
    tabPatterns: "🧩 Patrones",
    tabCol: "🎨 Colores",
    tabGuia: "📖 Guía de cruces",
    tabTutorial: "🌟 Tutoriales",
    searchPlaceholder: "🔍 Buscar flor...",
    addFlower: "+ Nueva flor",
    regSubtitle: (n) => `${n} flores · Toca el recuadro de imagen para añadir foto 📷`,
    evtSubtitle: (n) => `${n} flores de eventos · Fechas orientativas 📅`,
    colSubtitle: "Los 29 colores · cómo obtenerlos",
    autoSave: "💾 Tu progreso se guarda automáticamente en la nube ☁️",
    guideCredit: "Guías creadas por",
    trackerCredit: "Tracker hecho con ayuda de",
    baseColors: "🌱 Colores base",
    allColors: "Todos los 29 colores",
    markAll: "✓ Marcar todos",
    unmarkAll: "✕ Desmarcar todos",
    showColors: (n) => `▼ Ver todos los colores (${n} pendientes)`,
    hideColors: "▲ Ocultar todos",
    pattern: "Patrón",
    newBadge: "nueva ✨",
    addPhotoTip: "+ foto",
    onlyTransfer: "Solo transferencia 🔄",
    loading: "Cargando tu jardín...",
    addFlowerTitle: "🌱 Añadir nueva flor",
    flowerType: "Tipo de flor",
    typeRegional: "🌿 Regional",
    typeEvent: "🎉 Evento",
    nameEs: "Nombre en español *",
    nameEn: "Nombre en inglés",
    nameEsPlaceholder: "ej. Campanosa",
    nameEnPlaceholder: "ej. Bellbutton",
    patternLabel: "Patrón por defecto",
    patternPlaceholder: "ej. degradado, purpurina...",
    originLabel: "Cómo y dónde obtenerla",
    originPlaceholder: "ej. Semillas en La Pradera...",
    eventName: "Nombre del evento",
    eventNamePlaceholder: "ej. Amor y abrazos",
    eventDates: "Fechas",
    eventDatesPlaceholder: "ej. 8–21 feb",
    baseColorsOptional: "Colores base (opcional)",
    cancel: "Cancelar",
    addBtn: "🌸 Añadir flor",
    tabVisitors: "🐾 Visitantes",
  },
  en: {
    appSubtitle: "Flower Collector 🌷",
    totalProgress: "Total progress",
    colors: "colors",
    logout: "Log out",
    tabReg: "🌿 Regional",
    tabEvt: "🎉 Events",
    tabPatterns: "🧩 Patterns",
    tabCol: "🎨 Colors",
    tabGuia: "📖 Breeding Guide",
    tabTutorial: "🌟 Tutorials",
    searchPlaceholder: "🔍 Search flower...",
    addFlower: "+ New flower",
    regSubtitle: (n) => `${n} flowers · Tap the image box to add a photo 📷`,
    evtSubtitle: (n) => `${n} event flowers · Approximate dates 📅`,
    colSubtitle: "All 29 colors · how to get them",
    autoSave: "💾 Your progress saves automatically to the cloud ☁️",
    guideCredit: "Guides by",
    trackerCredit: "Tracker made with help from",
    baseColors: "🌱 Base colors",
    allColors: "All 29 colors",
    markAll: "✓ Mark all",
    unmarkAll: "✕ Unmark all",
    showColors: (n) => `▼ Show all colors (${n} remaining)`,
    hideColors: "▲ Hide all",
    pattern: "Pattern",
    newBadge: "new ✨",
    addPhotoTip: "+ photo",
    onlyTransfer: "Transfer only 🔄",
    loading: "Loading your garden...",
    addFlowerTitle: "🌱 Add new flower",
    flowerType: "Flower type",
    typeRegional: "🌿 Regional",
    typeEvent: "🎉 Event",
    nameEs: "Spanish name *",
    nameEn: "English name",
    nameEsPlaceholder: "e.g. Campanosa",
    nameEnPlaceholder: "e.g. Bellbutton",
    patternLabel: "Default pattern",
    patternPlaceholder: "e.g. gradient, glitter...",
    originLabel: "How and where to get it",
    originPlaceholder: "e.g. Seeds at The Meadow...",
    eventName: "Event name",
    eventNamePlaceholder: "e.g. Love & Hugs",
    eventDates: "Dates",
    eventDatesPlaceholder: "e.g. Feb 8–21",
    baseColorsOptional: "Base colors (optional)",
    cancel: "Cancel",
    addBtn: "🌸 Add flower",
    tabVisitors: "🐾 Visitors",
  }
};


const COLORS = [
  {id:"blanco",es:"Blanco",en:"White",hex:"#FFFFFF"},{id:"gris",es:"Gris",en:"Gray",hex:"#C0B8C8"},{id:"negro",es:"Negro",en:"Black",hex:"#3A3040"},{id:"rojo",es:"Rojo",en:"Red",hex:"#E8404A"},{id:"rosa_calido",es:"Rosa cálido",en:"Warm Pink",hex:"#F4A0B0"},{id:"coral",es:"Coral",en:"Coral",hex:"#F07858"},{id:"rosaceo",es:"Rosáceo",en:"Blush",hex:"#F5C8C0"},{id:"naranja",es:"Naranja",en:"Orange",hex:"#F5922A"},{id:"melocoton",es:"Melocotón",en:"Peach",hex:"#FBC8A0"},{id:"amarillo",es:"Amarillo",en:"Yellow",hex:"#F5D800"},{id:"crema",es:"Crema",en:"Cream",hex:"#F8EEB8"},{id:"lima",es:"Lima",en:"Lime",hex:"#A8D840"},{id:"pistacho",es:"Pistacho",en:"Pistachio",hex:"#D0E8A0"},{id:"verde",es:"Verde",en:"Green",hex:"#48B870"},{id:"menta",es:"Menta",en:"Mint",hex:"#A8E0C8"},{id:"verde_azulado",es:"Verde azulado",en:"Teal",hex:"#38A8A0"},{id:"espuma_mar",es:"Espuma de mar",en:"Seafoam",hex:"#B0E8E0"},{id:"celeste",es:"Celeste",en:"Sky",hex:"#70C8F0"},{id:"nube",es:"Nube",en:"Cloud",hex:"#C8E8F8"},{id:"azul",es:"Azul",en:"Blue",hex:"#3870D8"},{id:"hielo",es:"Hielo",en:"Ice",hex:"#B8D0F0"},{id:"anil",es:"Añíl",en:"Indigo",hex:"#5850C0"},{id:"bigaro",es:"Bígaro",en:"Periwinkle",hex:"#B0A8E0"},{id:"violeta",es:"Violeta",en:"Violet",hex:"#9048C8"},{id:"lila",es:"Lila",en:"Lilac",hex:"#D0A8E8"},{id:"magenta",es:"Magenta",en:"Magenta",hex:"#D040A0"},{id:"rosa_frio",es:"Rosa frío",en:"Cool Pink",hex:"#F0A8D0"},{id:"rosa_intenso",es:"Rosa intenso",en:"Hot Pink",hex:"#F040A0"},{id:"rosa",es:"Rosa",en:"Pink",hex:"#F8B8D0"}
];

const COLOR_MIX = {gris:"negro + blanco",rosa_calido:"rojo + blanco",coral:"rojo + naranja",rosaceo:"coral + blanco",naranja:"rojo + amarillo",melocoton:"naranja + blanco",crema:"amarillo + blanco",lima:"amarillo + verde",pistacho:"lima + blanco",verde:"amarillo + azul",menta:"verde + blanco",verde_azulado:"verde + celeste",espuma_mar:"verde azulado + blanco",nube:"celeste + blanco",hielo:"azul + blanco",anil:"azul + violeta",bigaro:"añíl + blanco",violeta:"rojo + azul",lila:"violeta + blanco",magenta:"violeta + rosa intenso",rosa_frio:"magenta + blanco",rosa:"rosa intenso + blanco"};

const DEFAULT_REGIONALES = [
  {id:"campanosa",es:"Campanosa",en:"Bellbutton",def:["blanco","amarillo","azul"],patron:"Degradado",patronEn:"Ombre",origen:"Semillas en La Pradera / Cápsula de Semillas",origenEn:"Seeds at Merry Meadows / Seed Capsule",tipo:"regional"},
  {id:"diente_lirio",es:"Diente de lirio",en:"Dandelily",def:["blanco","rojo","amarillo"],patron:"Degradado",patronEn:"Ombre",origen:"Semillas en La Pradera / Cápsula de Semillas",origenEn:"Seeds at Merry Meadows / Seed Capsule",tipo:"regional"},
  {id:"matriarita",es:"Matriarita",en:"Penstemum",def:["blanco","amarillo","azul"],patron:"Degradado",patronEn:"Ombre",origen:"Semillas en La Pradera / Cápsula de Semillas",origenEn:"Seeds at Merry Meadows / Seed Capsule",tipo:"regional"},
  {id:"tulias",es:"Tulias",en:"Tulias",def:["blanco","amarillo","azul"],patron:"Degradado",patronEn:"Ombre",origen:"Semillas en La Pradera / Cápsula de Semillas",origenEn:"Seeds at Merry Meadows / Seed Capsule",tipo:"regional"},
  {id:"hibisco",es:"Hibisco",en:"Hibiscus",def:["rosa_intenso"],patron:"Degradado",patronEn:"Ombre",origen:"Complejo Playero · Beach Blossoms con Lou",origenEn:"Seaside Resort · Beach Blossoms with Lou",tipo:"regional"},
  {id:"brillo_fantasmal",es:"Brillo fantasmal",en:"Ghostgleam",def:["blanco"],patron:"Degradado",patronEn:"Ombre",origen:"Pantano Espantoso · Fen Flora con Aro",origenEn:"Spooky Swamp · Fen Flora with Aro",tipo:"regional"},
  {id:"cardo",es:"Cardo",en:"Thistle",def:["violeta"],patron:"Degradado",patronEn:"Ombre",origen:"Montaña de las Gemas · Wild Mountain Time con Poco",origenEn:"Gemstone Mountain · Wild Mountain Time with Poco",tipo:"regional"},
  {id:"ortiga",es:"Ortiga",en:"Heavy Nettle",def:["naranja"],patron:"Degradado",patronEn:"Ombre",origen:"Monte Impulsivo · Fiery Flowers con Chico",origenEn:"Mount Hothead · Fiery Flowers with Chico",tipo:"regional"},
  {id:"anemona",es:"Anémona",en:"Anemone",def:["lima","magenta"],patron:"Degradado",patronEn:"Ombre",origen:"Arrecife Arcoíris · Reefside Riches con Marin",origenEn:"Rainbow Reef · Reefside Riches with Marin",tipo:"regional"},
  {id:"suenopompo",es:"Sueñopompó",en:"Dreampuff",def:["menta","nube","bigaro","rosa_frio"],patron:"Degradado",patronEn:"Ombre",origen:"Isla Nube · Flowers of our Labor con Roseanne",origenEn:"Cloud Island · Flowers of our Labor with Roseanne",tipo:"regional"},
  {id:"florina",es:"Florina",en:"Wheatflower",def:["crema"],patron:"Purpurina",patronEn:"Glitter",origen:"Trigal Sensacional · Potion Problems",origenEn:"City Town · Potion Problems",tipo:"regional"},
  {id:"pluma_escarcha",es:"Pluma de escarcha",en:"Frostfeather",def:["hielo"],patron:"Escarcha",patronEn:"Frost",origen:"Pico Helado · Extremophile Flora con Nina",origenEn:"Icy Peak · Extremophile Flora with Nina",tipo:"regional"},
  {id:"bulbo_igneo",es:"Bulbo ígneo",en:"Blazebulb",def:["coral","rosaceo","melocoton","crema"],patron:"Fundido",patronEn:"Molten",origen:"Caldera · Dragon's Breath con Nina",origenEn:"Mount Hothead · Dragon's Breath with Nina",tipo:"regional"},
  {id:"crystalia",es:"Crystalia",en:"Crystalia",def:["pistacho","verde_azulado","rosa"],patron:"Cristal",patronEn:"Crystal",origen:"Cuevas de cristal · Forgotten Flower con Nina",origenEn:"Gemstone Mountain · Forgotten Flower with Nina",tipo:"regional"},
  {id:"bubbaluna",es:"Bubbaluna",en:"Bubbaluna",def:["rosa","anil"],patron:"Cósmico",patronEn:"Cosmic",origen:"La Luna · A Finishing Flourish con Nina",origenEn:"Cloud Island · A Finishing Flourish with Nina",tipo:"regional"},
  {id:"destello_solar",es:"Destello solar",en:"Sunburst",def:["naranja"],patron:"Destello solar",patronEn:"Sunbeam",origen:"Semillas en Trigal Sensacional",origenEn:"Seeds in City Town",tipo:"regional"}
];

const DEFAULT_EVENTOS = [
  {id:"rosa_event",es:"Rosa",en:"Rose",def:["blanco","rojo","amarillo","rosa"],patron:"Anillo",patronEn:"Ring",eventoEs:"Amor y abrazos",evento:"Love & Hugs",fechas:"8–21 feb",tipo:"evento"},
  {id:"narcilegre",es:"Narcilegre",en:"Happadil",def:["amarillo","rosa_intenso"],patron:"Confeti",patronEn:"Confetti",eventoEs:"Días de acogida y alegría",evento:"Welcome & Cheer Days",fechas:"17–23 mar",tipo:"evento"},
  {id:"cadendula",es:"Caléndula",en:"Marigold",def:["naranja"],patron:"Ribeteado",patronEn:"Trim",eventoEs:"Carnaval multicolor",evento:"Multicolor Carnival",fechas:"25 mar–12 abr",tipo:"evento"},
  {id:"hueflor",es:"Hueflor",en:"Eggwort",def:["blanco","celeste"],patron:"Moteado",patronEn:"Speckled",eventoEs:"Mes de Meh",evento:"Meh Month",fechas:"25 mar–12 abr",tipo:"evento"},
  {id:"molinillo",es:"Molinillo",en:"Pinwheel",def:["coral","lima","anil"],patron:"Alternativo",patronEn:"Alternate",eventoEs:"Desfile de folios",evento:"Folio Parade",fechas:"13 jun–1 jul",tipo:"evento"},
  {id:"petunia",es:"Petunia",en:"Petunia",def:["magenta"],patron:"A rayas",patronEn:"Striped",eventoEs:"Mermelada multitudinaria",evento:"Jam Jamboree",fechas:"12–26 sep",tipo:"evento"},
  {id:"flor_lacito",es:"Flor de lacito",en:"Bowblossom",def:["rosa_calido","espuma_mar","lila"],patron:"Iridiscente",patronEn:"Iridescent",eventoEs:"Interludio de sueños",evento:"Dream Interlude",fechas:"19 oct–15 nov",tipo:"evento"},
  {id:"flor_pascua",es:"Flor de Pascua",en:"Poinsettia",def:["blanco","rojo"],patron:"Manchas",patronEn:"Patch",eventoEs:"Regalos para todos",evento:"Gifts for Everyone",fechas:"24 nov–29 dic",tipo:"evento"},
  {id:"bolaluz",es:"Bolaluz",en:"Glowbal",def:["amarillo","celeste","rosa_intenso"],patron:"Brillo",patronEn:"Glow",eventoEs:"Fiesta lumínica",evento:"Light Festival",fechas:"19 dic–15 ene",tipo:"evento"}
];

const cm = Object.fromEntries(COLORS.map(c => [c.id, c]));

function isDark(h) {
  const r = parseInt(h.slice(1,3),16), g = parseInt(h.slice(3,5),16), b = parseInt(h.slice(5,7),16);
  return (r*299 + g*587 + b*114) / 1000 < 148;
}

// ─── Supabase helpers ────────────────────────────────────────────────────────

async function loadProgress(userId) {
  const { data } = await supabase.from('flower_progress').select('flower_id,color_id').eq('user_id', userId);
  const prog = {};
  (data || []).forEach(({ flower_id, color_id }) => {
    if (!prog[flower_id]) prog[flower_id] = {};
    prog[flower_id][color_id] = true;
  });
  return prog;
}

async function loadPhotos(userId) {
  const { data } = await supabase.from('flower_photos').select('flower_id,photo_data').eq('user_id', userId);
  const photos = {};
  (data || []).forEach(({ flower_id, photo_data }) => { photos[flower_id] = photo_data; });
  return photos;
}

async function loadCustomFlowers(userId) {
  const { data } = await supabase.from('custom_flowers').select('id,flower_data').eq('user_id', userId);
  return (data || []).map(row => ({ ...row.flower_data, supabase_id: row.id }));
}

async function toggleProgressDB(userId, flowerId, colorId, checked) {
  if (checked) {
    await supabase.from('flower_progress').delete().eq('user_id', userId).eq('flower_id', flowerId).eq('color_id', colorId);
  } else {
    await supabase.from('flower_progress').upsert({ user_id: userId, flower_id: flowerId, color_id: colorId });
  }
}

async function savePhotoDb(userId, flowerId, photoData) {
  await supabase.from('flower_photos').upsert({ user_id: userId, flower_id: flowerId, photo_data: photoData, updated_at: new Date().toISOString() });
}

async function addCustomFlowerDb(userId, flowerData) {
  const { data } = await supabase.from('custom_flowers').insert({ user_id: userId, flower_data: flowerData }).select().single();
  return data;
}

async function deleteCustomFlowerDb(supabaseId) {
  await supabase.from('custom_flowers').delete().eq('id', supabaseId);
}

// ─── Components ──────────────────────────────────────────────────────────────


const FLOWER_IMG = {
  // Regionales
  "anemona":        "Anemona-Anemone",
  "brillo_fantasmal":"Brillo_fantasmal-Ghostgleam",
  "bubbaluna":      "Bubbaluna",
  "bulbo_igneo":    "Bulbo_igneo-Blazebulb",
  "campanosa":      "Campanosa-Bellbutton",
  "cardo":          "Cardo-Thistle",
  "crystalia":      "Crystalia",
  "diente_lirio":   "Diente_de_lirio-Dandelily",
  "florina":        "Florina-Wheatflower",
  "hibisco":        "Hibisco-Hibiscus",
  "matriarita":     "Matriarita-Penstemum",
  "ortiga":         "Ortiga-Heavy_Nettle",
  "pluma_escarcha": "Pluma_de_escarcha-Frostfeather",
  "suenopompo":     "Suenopompo-Dreampuff",
  "tulias":         "Tulias",
  // Eventos
  "bolaluz":        "Bolaluz-Glowbal",
  "cadendula":      "Cadendula-Marigold",
  "flor_lacito":    "Flor_de_lacito-Bowblossom",
  "flor_pascua":    "Flor_de_Pascua-Poinsettia",
  "hueflor":        "Huevoflor-Eggwort",
  "molinillo":      "Molinillo-Pinwheel",
  "narcilegre":     "Narcilegre-Happadil",
  "petunia":        "Petunia",
  "rosa_event":     "Rosa-Rose",
  "destello_solar": "Destello_solar-Sunburst",
};

function flowerImgUrl(id) {
  const file = FLOWER_IMG[id];
  if (!file) return null;
  return `https://hkia.microjessi.com/img/flowers/${file}.png`;
}

function FlowerPhoto({ flowerId, size = 56 }) {
  const [imgError, setImgError] = useState(false);
  const url = flowerImgUrl(flowerId);
  return (
    <div style={{ width: size, height: size, flexShrink: 0, borderRadius: 14, overflow: "hidden", border: "2px solid rgba(180,130,220,0.2)", background: "linear-gradient(135deg,#f0e0f8,#e8d0f0)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      {url && !imgError
        ? <img src={url} alt="flor" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={() => setImgError(true)} />
        : <span style={{ fontSize: 26 }}>🌸</span>}
    </div>
  );
}

function Pill({ id, checked, onToggle, lang = 'es' }) {
  const c = cm[id]; if (!c) return null;
  const dark = isDark(c.hex);
  return (
    <button onClick={() => onToggle(id)}
      style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 9px", borderRadius: 99, border: checked ? "2px solid rgba(0,0,0,0.18)" : "1.5px solid #ead8f0", background: checked ? c.hex : "#f8f0fc", color: checked ? (dark ? "#fff" : "#333") : "#c0a0d0", fontSize: 11, fontWeight: 700, cursor: "pointer", transition: "all 0.15s", boxShadow: checked ? "0 1px 5px rgba(0,0,0,0.1)" : "none" }}>
      <span style={{ width: 9, height: 9, borderRadius: "50%", background: c.hex, border: "1.5px solid rgba(0,0,0,0.15)", display: "inline-block", flexShrink: 0 }} />
      {c[lang] || c.es}{checked && <span style={{ fontSize: 9 }}>✓</span>}
    </button>
  );
}

const lbl = { display: "block", fontSize: 10, fontWeight: 700, color: "#c0a0d0", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 };
const inp = { width: "100%", padding: "9px 12px", borderRadius: 10, border: "1.5px solid #e8d0f0", fontSize: 13, color: "#7a5090", outline: "none", boxSizing: "border-box", background: "#faf5ff", fontFamily: "Nunito, sans-serif" };

function Card({ f, prog, onToggle, onDelete, isEvent = false, lang = 'es' }) {
  const [exp, setExp] = useState(false);
  const t = T[lang];
  const done = COLORS.filter(c => prog[c.id]).length;
  const ac = isEvent ? "#c050a0" : "#4888c0";
  const allDone = COLORS.every(c => prog[c.id]);
  return (
    <div style={{ background: isEvent ? "linear-gradient(135deg,#fff8fe,#fdf0fc)" : "linear-gradient(135deg,#f6fbff,#eef6fe)", borderRadius: 20, border: `1.5px solid ${isEvent ? "#f0c8ec" : "#c8e0f4"}`, boxShadow: "0 2px 14px rgba(180,130,220,0.1)", padding: "16px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <FlowerPhoto flowerId={f.id} size={56} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 900, fontSize: 15, color: "#5a3870" }}>{lang === 'en' && f.en ? f.en : f.es}</span>
            <span style={{ fontSize: 11, color: "#c0a0c8", fontStyle: "italic" }}>{lang === 'en' ? f.es : f.en}</span>
            {f.custom && <span style={{ fontSize: 9, background: "#f0e0ff", color: "#a060d0", borderRadius: 6, padding: "1px 6px", fontWeight: 700 }}>{t.newBadge}</span>}
          </div>
          {isEvent
            ? <div style={{ fontSize: 11, color: ac, fontWeight: 700 }}>🎉 {lang === "en" ? f.evento : (f.eventoEs || f.evento)} · {f.fechas}</div>
            : <div style={{ fontSize: 10, color: "#b0a0c0", marginTop: 1 }}>{lang === "en" ? (f.origenEn || f.origen) : f.origen}</div>}
          <div style={{ fontSize: 10, color: "#c8b0d8" }}>{t.pattern}: <b>{lang === "en" ? (f.patronEn || f.patron) : f.patron}</b></div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          <div style={{ background: done === COLORS.length ? "#d8f0e0" : "rgba(0,0,0,0.05)", borderRadius: 10, padding: "4px 10px", textAlign: "center" }}>
            <div style={{ fontSize: 15, fontWeight: 900, color: done === COLORS.length ? "#38a058" : ac }}>{done}</div>
            <div style={{ fontSize: 9, color: "#c0b0d0" }}>/{COLORS.length}</div>
          </div>
          {f.custom && <button onClick={onDelete} style={{ background: "#fff0f4", border: "1px solid #f0c0cc", borderRadius: 8, cursor: "pointer", fontSize: 12, color: "#d06070", padding: "3px 7px", fontWeight: 700 }}>🗑️</button>}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1, height: 8, borderRadius: 99, background: "rgba(0,0,0,0.07)", overflow: "hidden" }}>
          <div style={{ width: `${Math.round(done / COLORS.length * 100)}%`, height: "100%", background: ac, borderRadius: 99, transition: "width 0.4s" }} />
        </div>
        <span style={{ fontSize: 11, color: "#b090c0", fontWeight: 700, minWidth: 36, textAlign: "right" }}>{done}/{COLORS.length}</span>
      </div>
      <div>
        <div style={{ fontSize: 10, color: "#c8a8d0", fontWeight: 700, marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5 }}>{t.baseColors}</div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>{f.def.map(id => <Pill key={id} id={id} checked={!!prog[id]} onToggle={onToggle} lang={lang} />)}</div>
      </div>
      <button onClick={() => setExp(e => !e)}
        style={{ background: "none", border: `1.5px dashed ${ac}44`, borderRadius: 10, padding: "5px 10px", color: ac, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
        {exp ? t.hideColors : t.showColors(COLORS.length - done)}
      </button>
      {exp && (
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 10, color: "#c8a8d0", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{t.allColors}</div>
            <button
              onClick={() => { COLORS.forEach(c => { if (allDone ? prog[c.id] : !prog[c.id]) onToggle(c.id); }); }}
              style={{ fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 99, border: "none", background: allDone ? "#f0d8f8" : "linear-gradient(135deg,#e070c8,#8858d0)", color: allDone ? "#a060c0" : "#fff", cursor: "pointer", transition: "all 0.2s" }}>
              {allDone ? t.unmarkAll : t.markAll}
            </button>
          </div>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>{COLORS.map(c => <Pill key={c.id} id={c.id} checked={!!prog[c.id]} onToggle={onToggle} lang={lang} />)}</div>
        </div>
      )}
    </div>
  );
}

function AddFlowerModal({ onAdd, onClose, lang = 'es' }) {
  const t = T[lang];
  const [form, setForm] = useState({ es: "", en: "", patron: "degradado", origen: "", evento: "", fechas: "", tipo: "regional", def: [] });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleDef = (id) => setForm(f => ({ ...f, def: f.def.includes(id) ? f.def.filter(x => x !== id) : [...f.def, id] }));
  const valid = form.es.trim().length > 0;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(80,40,120,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 24, padding: 24, width: "100%", maxWidth: 500, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(120,60,200,0.25)" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 900, color: "#5a3870" }}>{t.addFlowerTitle}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#c0a0d0", lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={lbl}>{t.flowerType}</label>
            <div style={{ display: "flex", gap: 8 }}>
              {["regional", "evento"].map(tp => (
                <button key={tp} onClick={() => set("tipo", tp)} style={{ flex: 1, padding: "9px", borderRadius: 12, border: `2px solid ${form.tipo === tp ? "#c070d8" : "#e8d0f0"}`, background: form.tipo === tp ? "linear-gradient(135deg,#f0d0ff,#e8c8f8)" : "#faf5ff", color: form.tipo === tp ? "#7030a0" : "#c0a0d0", fontWeight: 800, fontSize: 12, cursor: "pointer", transition: "all 0.15s", fontFamily: "Nunito, sans-serif" }}>
                  {tp === "regional" ? t.typeRegional : t.typeEvent}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}><label style={lbl}>{t.nameEs}</label><input value={form.es} onChange={e => set("es", e.target.value)} placeholder={t.nameEsPlaceholder} style={inp} /></div>
            <div style={{ flex: 1 }}><label style={lbl}>{t.nameEn}</label><input value={form.en} onChange={e => set("en", e.target.value)} placeholder={t.nameEnPlaceholder} style={inp} /></div>
          </div>
          <div><label style={lbl}>{t.patternLabel}</label><input value={form.patron} onChange={e => set("patron", e.target.value)} placeholder={t.patternPlaceholder} style={inp} /></div>
          {form.tipo === "regional"
            ? <div><label style={lbl}>{t.originLabel}</label><input value={form.origen} onChange={e => set("origen", e.target.value)} placeholder={t.originPlaceholder} style={inp} /></div>
            : <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 2 }}><label style={lbl}>{t.eventName}</label><input value={form.evento} onChange={e => set("evento", e.target.value)} placeholder={t.eventNamePlaceholder} style={inp} /></div>
                <div style={{ flex: 1 }}><label style={lbl}>{t.eventDates}</label><input value={form.fechas} onChange={e => set("fechas", e.target.value)} placeholder={t.eventDatesPlaceholder} style={inp} /></div>
              </div>}
          <div>
            <label style={lbl}>{t.baseColorsOptional}</label>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 4 }}>
              {COLORS.map(c => {
                const sel = form.def.includes(c.id);
                const dark = isDark(c.hex);
                return <button key={c.id} onClick={() => toggleDef(c.id)} style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "3px 8px", borderRadius: 99, border: sel ? "2px solid rgba(0,0,0,0.18)" : "1.5px solid #ead8f0", background: sel ? c.hex : "#f8f0fc", color: sel ? (dark ? "#fff" : "#333") : "#c0a0d0", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "Nunito, sans-serif" }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: c.hex, border: "1.5px solid rgba(0,0,0,0.15)", display: "inline-block" }} />{c[lang] || c.es}
                </button>;
              })}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "11px", borderRadius: 12, border: "1.5px solid #e8d0f0", background: "#faf5ff", color: "#c0a0d0", fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "Nunito, sans-serif" }}>{t.cancel}</button>
          <button onClick={() => { if (valid) onAdd(form); }} disabled={!valid}
            style={{ flex: 2, padding: "11px", borderRadius: 12, border: "none", background: valid ? "linear-gradient(135deg,#e070c8,#8858d0)" : "#e8d8f0", color: valid ? "#fff" : "#c0b0d0", fontWeight: 800, fontSize: 13, cursor: valid ? "pointer" : "default", fontFamily: "Nunito, sans-serif", boxShadow: valid ? "0 2px 10px #d090d055" : "none" }}>
            {t.addBtn}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Visitor Data ─────────────────────────────────────────────────────────────

const ZONES = [
  { name: "City Town",            file: "City_Town_Icon" },
  { name: "Cloud Island",         file: "Cloud_Island_Icon" },
  { name: "Crystal Caves",        file: "Crystal_Caves" },
  { name: "Gemstone Mountain",    file: "Gemstone_Mountain_Icon" },
  { name: "Greenhouse",           file: "Greenhouse_Icon" },
  { name: "Icy Peak",             file: "Icy_Peak" },
  { name: "Merry Meadows",        file: "Merry_Meadow_Icon" },
  { name: "Mount Hothead",        file: "Mount_Hothead_Icon" },
  { name: "Rainbow Reef",         file: "Rainbow_Reef_Icon" },
  { name: "Seaside Resort",       file: "Seaside_Resort_Icon" },
  { name: "Spooky Swamp",         file: "Spooky_Swamp_Icon" },
  { name: "The Moon",             file: "The_Moon" },
  { name: "Wheatflour Wonderland",file: "Wheatflour_Wonderland" },
];

function zoneImgUrl(file) {
  return `https://hkia.microjessi.com/img/locations/${file}.webp`;
}

const DEFAULT_VISITORS = [
  "Ajimaru",
  "Aro",
  "Azuki",
  "Baku",
  "Berry",
  "Buppi",
  "Cappuccino",
  "Carl",
  "Cherry",
  "Chico",
  "Chief Komiya",
  "Chiffon",
  "Coco",
  "Corune",
  "Dear Daniel",
  "Espresso",
  "Fenneko",
  "Ganta",
  "George White",
  "Gori",
  "Gyoniku",
  "Haida",
  "Hello Kitty's Grandma",
  "Hello Kitty's Grandpa",
  "Hidarin",
  "Kabae",
  "Kanikama",
  "Koroppi",
  "Kuma",
  "Kyorosuke",
  "Lou",
  "Lutz",
  "Macaron",
  "Manaka",
  "Marin",
  "Mary White",
  "Mashu",
  "Migi",
  "Milk",
  "Mimmy",
  "Mocha",
  "My Melody's Grandma",
  "My Melody's Grandpa",
  "My Melody's Mama",
  "My Melody's Papa",
  "Neko",
  "Nina",
  "Noberun",
  "Nuts",
  "Nyako",
  "Pam",
  "Panya",
  "Pikki",
  "Poco",
  "Pompompurin's Mama",
  "Pompompurin's Papa",
  "Poron",
  "Puko",
  "Retsuko's Mom",
  "Roseanne",
  "Ruby",
  "Sora",
  "Tam",
  "Tsunoda",
  "Wanwa",
  "Washimi",
  "Yoga Instructor"
].sort();

const VISITOR_IMG = {
  "Ajimaru": "Ajimaru",
  "Aro": "Aro",
  "Azuki": "Azuki_Icon",
  "Baku": "Baku",
  "Berry": "Berry",
  "Buppi": "Buppi",
  "Cappuccino": "Cappuccino",
  "Carl": "Carl",
  "Cherry": "Cherry",
  "Chico": "Chico",
  "Chief Komiya": "Chief_Komiya",
  "Chiffon": "Chiffon",
  "Coco": "Coco",
  "Corune": "Corune",
  "Dear Daniel": "Dear_Daniel-Icon",
  "Espresso": "Espresso",
  "Fenneko": "Fenneko",
  "Ganta": "Ganta",
  "George White": "George_White_Icon",
  "Gori": "Gori",
  "Gyoniku": "Gyoniku",
  "Haida": "Haida",
  "Hello Kitty's Grandma": "Hello_Kitty's_Grandma",
  "Hello Kitty's Grandpa": "Hello_Kitty's_Grandpa",
  "Hidarin": "Hidarin",
  "Kabae": "Kabae",
  "Kanikama": "Kanikama",
  "Koroppi": "Koroppi",
  "Kuma": "Kuma",
  "Kyorosuke": "Kyorosuke",
  "Lou": "Lou",
  "Lutz": "Lutz",
  "Macaron": "Macaron_Icon",
  "Manaka": "Manaka",
  "Marin": "Marin",
  "Mary White": "Mary_White",
  "Mashu": "Mashu",
  "Migi": "Migi",
  "Milk": "Milk_Icon",
  "Mimmy": "Mimmy",
  "Mocha": "Mocha_Icon",
  "My Melody's Grandma": "My_Melodys_Grandma",
  "My Melody's Grandpa": "My_Melodys_Grandpa",
  "My Melody's Mama": "My_Melodys_Mama",
  "My Melody's Papa": "My_Melodys_Papa",
  "Neko": "Neko",
  "Nina": "Nina",
  "Noberun": "Noberun",
  "Nuts": "Nuts",
  "Nyako": "Nyako",
  "Pam": "Pam",
  "Panya": "Panya",
  "Pikki": "Pikki",
  "Poco": "Poco",
  "Pompompurin's Mama": "Pompompurin's_Mama_Icon",
  "Pompompurin's Papa": "Pompompurin's_Papa_Icon",
  "Poron": "Poron",
  "Puko": "Puko",
  "Retsuko's Mom": "Retsuko's_Mom",
  "Roseanne": "Roseanne",
  "Ruby": "Ruby",
  "Sora": "Sora",
  "Tam": "Tam",
  "Tsunoda": "Tsunoda",
  "Wanwa": "Wanwa",
  "Washimi": "Washimi",
  "Yoga Instructor": "Yoga_Instructor"
};

function visitorImgUrl(name) {
  const file = VISITOR_IMG[name] || name.replace(/ /g, '_');
  return `https://hkia.microjessi.com/img/visitantes/${file}.webp`;
}

// ─── Supabase visitor helpers ─────────────────────────────────────────────────

async function loadVisitorProgress(userId) {
  const { data } = await supabase
    .from('visitor_progress')
    .select('visitor_id,zone,cabin,resident,notes')
    .eq('user_id', userId);
  const result = {};
  (data || []).forEach(row => { result[row.visitor_id] = row; });
  return result;
}

async function saveVisitorProgress(userId, visitorId, fields) {
  const payload = {
    user_id: userId,
    visitor_id: visitorId,
    zone: fields.zone || null,
    cabin: fields.cabin || null,
    resident: fields.resident || false,
    notes: fields.notes || null,
    updated_at: new Date().toISOString(),
  };
  const { error } = await supabase
    .from('visitor_progress')
    .upsert(payload, { onConflict: 'user_id,visitor_id' });
  if (error) console.error('saveVisitorProgress error:', error.message, error.details);
}

async function deleteVisitorProgress(userId, visitorId) {
  await supabase.from('visitor_progress').delete()
    .eq('user_id', userId).eq('visitor_id', visitorId);
}

// ─── ZonePicker ───────────────────────────────────────────────────────────────

function ZonePicker({ value, onChange, lang, fieldStyle }) {
  const es = lang === 'es';
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selected = ZONES.find(z => z.name === value);

  return (
    <div ref={ref} style={{ flex: 2, position: "relative" }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ ...fieldStyle, display: "flex", alignItems: "center", gap: 7, cursor: "pointer", paddingRight: 28, userSelect: "none" }}>
        {selected
          ? <>
              <img src={zoneImgUrl(selected.file)} alt={selected.name}
                style={{ width: 20, height: 20, borderRadius: 5, objectFit: "cover", flexShrink: 0 }}
                onError={e => e.target.style.display = 'none'} />
              <span style={{ fontSize: 12, color: "#6a4080", flex: 1 }}>{selected.name}</span>
            </>
          : <span style={{ fontSize: 12, color: "#c0a0d0" }}>{es ? "— Zona —" : "— Zone —"}</span>}
        <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "#c0a0d0" }}>{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1.5px solid #e8d0f0", borderRadius: 10, boxShadow: "0 4px 20px rgba(120,60,200,0.15)", zIndex: 100, maxHeight: 240, overflowY: "auto" }}>
          <div onClick={() => { onChange(""); setOpen(false); }}
            style={{ padding: "8px 12px", fontSize: 12, color: "#c0a0d0", cursor: "pointer", borderBottom: "1px solid #f0e8f8" }}>
            {es ? "— Sin zona —" : "— No zone —"}
          </div>
          {ZONES.map(z => (
            <div key={z.name} onClick={() => { onChange(z.name); setOpen(false); }}
              style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 12px", cursor: "pointer", background: value === z.name ? "#f5eeff" : "transparent", borderBottom: "1px solid #f8f0ff", transition: "background 0.1s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#f5eeff"}
              onMouseLeave={e => e.currentTarget.style.background = value === z.name ? "#f5eeff" : "transparent"}>
              <img src={zoneImgUrl(z.file)} alt={z.name}
                style={{ width: 24, height: 24, borderRadius: 6, objectFit: "cover", flexShrink: 0 }}
                onError={e => e.target.style.display = 'none'} />
              <span style={{ fontSize: 12, color: "#6a4080", fontWeight: value === z.name ? 800 : 600 }}>{z.name}</span>
              {value === z.name && <span style={{ marginLeft: "auto", color: "#9050d0", fontSize: 11 }}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── VisitorCard ──────────────────────────────────────────────────────────────

function VisitorCard({ name, data = {}, onChange, onDelete, isCustom, lang }) {
  const es = lang === 'es';
  const [imgError, setImgError] = useState(false);
  const resident = !!data.resident;

  const fieldStyle = {
    width: "100%", padding: "6px 10px", borderRadius: 8,
    border: "1.5px solid #e8d8f4", fontSize: 12, color: "#6a4080",
    background: "rgba(255,255,255,0.85)", fontFamily: "Nunito, sans-serif",
    outline: "none", boxSizing: "border-box",
  };

  return (
    <div style={{
      background: resident
        ? "linear-gradient(135deg,#f0fff4,#e8fef0)"
        : "linear-gradient(135deg,#fdf8ff,#f8f0ff)",
      borderRadius: 18,
      border: `1.5px solid ${resident ? "#a8e8b8" : "#e0c8f8"}`,
      boxShadow: "0 2px 12px rgba(160,100,220,0.08)",
      padding: "14px 16px",
      display: "flex", gap: 12, alignItems: "flex-start",
    }}>
      {/* Avatar */}
      <div style={{ flexShrink: 0, width: 56, height: 56, borderRadius: 14, overflow: "hidden", border: "2px solid rgba(180,130,220,0.25)", background: "linear-gradient(135deg,#f0e0f8,#e8d0f0)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {!imgError
          ? <img src={visitorImgUrl(name)} alt={name} onError={() => setImgError(true)}
              style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <span style={{ fontSize: 26 }}>🐾</span>}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 7 }}>
        {/* Name row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
          <span style={{ fontWeight: 900, fontSize: 14, color: resident ? "#207040" : "#5a3070" }}>{name}</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {resident && <span style={{ fontSize: 9, background: "#c8f0d8", color: "#206040", borderRadius: 6, padding: "2px 7px", fontWeight: 800 }}>
              {es ? "✓ Residente" : "✓ Resident"}
            </span>}
            {isCustom && <button onClick={onDelete} style={{ background: "#fff0f4", border: "1px solid #f0c0cc", borderRadius: 8, cursor: "pointer", fontSize: 11, color: "#d06070", padding: "2px 6px", fontWeight: 700 }}>🗑️</button>}
          </div>
        </div>

        {/* Zone + Cabin row */}
        <div style={{ display: "flex", gap: 7 }}>
          <ZonePicker value={data.zone || ""} onChange={zone => onChange({ zone })} lang={lang} fieldStyle={fieldStyle} />
          <input
            value={data.cabin || ""}
            onChange={e => onChange({ cabin: e.target.value })}
            placeholder={es ? "Cabaña (ej. 2B)" : "Cabin (e.g. 2B)"}
            style={{ ...fieldStyle, flex: 1 }}
          />
        </div>

        {/* Resident checkbox */}
        <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", userSelect: "none" }}>
          <div onClick={() => onChange({ resident: !resident })}
            style={{ width: 18, height: 18, borderRadius: 5, border: `2px solid ${resident ? "#48b870" : "#d0b0e8"}`, background: resident ? "#48b870" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s", cursor: "pointer" }}>
            {resident && <span style={{ color: "#fff", fontSize: 11, fontWeight: 900, lineHeight: 1 }}>✓</span>}
          </div>
          <span style={{ fontSize: 12, color: "#8060a0", fontWeight: 700 }}>
            {es ? "Ya es residente" : "Already a resident"}
          </span>
        </label>

        {/* Notes */}
        <textarea
          value={data.notes || ""}
          onChange={e => onChange({ notes: e.target.value })}
          placeholder={es
            ? "Notas (ej: falta Tarta de manzana con lazo rojo)"
            : "Notes (e.g: missing Apple Pie with red bow)"}
          rows={2}
          style={{ ...fieldStyle, resize: "vertical", lineHeight: 1.5 }}
        />
      </div>
    </div>
  );
}

// ─── AddVisitorModal ──────────────────────────────────────────────────────────

function AddVisitorModal({ onAdd, onClose, lang, existingNames }) {
  const es = lang === 'es';
  const [name, setName] = useState("");
  const valid = name.trim().length > 0 && !existingNames.includes(name.trim());
  const inp = { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1.5px solid #e8d0f0", fontSize: 13, color: "#7a5090", outline: "none", boxSizing: "border-box", background: "#faf5ff", fontFamily: "Nunito, sans-serif" };
  const lbl = { display: "block", fontSize: 10, fontWeight: 700, color: "#c0a0d0", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(80,40,120,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 24, padding: 24, width: "100%", maxWidth: 380, boxShadow: "0 8px 40px rgba(120,60,200,0.25)" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 900, color: "#5a3870" }}>
            {es ? "🐾 Añadir visitante" : "🐾 Add visitor"}
          </h2>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#c0a0d0" }}>✕</button>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>{es ? "Nombre del visitante *" : "Visitor name *"}</label>
          <input value={name} onChange={e => setName(e.target.value)}
            placeholder={es ? "ej. Cinnamoroll" : "e.g. Cinnamoroll"} style={inp} />
          {name.trim() && existingNames.includes(name.trim()) && (
            <p style={{ fontSize: 11, color: "#e05050", margin: "4px 0 0", fontWeight: 600 }}>
              {es ? "Este visitante ya existe" : "This visitor already exists"}
            </p>
          )}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "11px", borderRadius: 12, border: "1.5px solid #e8d0f0", background: "#faf5ff", color: "#c0a0d0", fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "Nunito, sans-serif" }}>
            {es ? "Cancelar" : "Cancel"}
          </button>
          <button onClick={() => { if (valid) onAdd(name.trim()); }} disabled={!valid}
            style={{ flex: 2, padding: "11px", borderRadius: 12, border: "none", background: valid ? "linear-gradient(135deg,#e070c8,#8858d0)" : "#e8d8f0", color: valid ? "#fff" : "#c0b0d0", fontWeight: 800, fontSize: 13, cursor: valid ? "pointer" : "default", fontFamily: "Nunito, sans-serif" }}>
            {es ? "🐾 Añadir" : "🐾 Add"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── VisitorsTab ──────────────────────────────────────────────────────────────

function VisitorsTab({ userId, lang }) {
  const es = lang === 'es';
  const [visitors, setVisitors] = useState(DEFAULT_VISITORS.map(n => ({ name: n, custom: false })));
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const saveTimers = useRef({});

  useEffect(() => {
    (async () => {
      const data = await loadVisitorProgress(userId);
      setProgress(data);
      // Load custom visitors stored in progress with custom:true flag
      const customNames = Object.keys(data).filter(k => data[k]?.custom && !DEFAULT_VISITORS.includes(k));
      if (customNames.length) {
        setVisitors(v => {
          const existing = new Set(v.map(x => x.name));
          const newOnes = customNames.filter(n => !existing.has(n)).map(n => ({ name: n, custom: true }));
          return [...v, ...newOnes].sort((a, b) => a.name.localeCompare(b.name));
        });
      }
      setLoading(false);
    })();
  }, [userId]);

  // Save visitor progress - debounce everything to avoid React async state issues
  function handleChange(name, fields) {
    // Update state immediately for UI
    const newProgress = { ...progress, [name]: { ...(progress[name] || {}), ...fields } };
    setProgress(newProgress);

    // Save to Supabase - debounce to batch rapid changes
    clearTimeout(saveTimers.current[name]);
    saveTimers.current[name] = setTimeout(() => {
      const merged = newProgress[name] || {};
      saveVisitorProgress(userId, name, merged);
    }, 500);
  }

  function handleAdd(name) {
    setVisitors(v => [...v, { name, custom: true }].sort((a, b) => a.name.localeCompare(b.name)));
    saveVisitorProgress(userId, name, { zone: "", cabin: "", resident: false, notes: "" });
    setShowAdd(false);
  }

  function handleDelete(name) {
    setVisitors(v => v.filter(x => x.name !== name));
    setProgress(p => { const n = { ...p }; delete n[name]; return n; });
    deleteVisitorProgress(userId, name);
  }

  const filtered = visitors.filter(v => !q || v.name.toLowerCase().includes(q.toLowerCase()));
  const residentCount = visitors.filter(v => progress[v.name]?.resident).length;

  if (loading) return (
    <div style={{ textAlign: "center", padding: "40px 0", color: "#c0a0c8", fontWeight: 700, fontSize: 13 }}>
      🐾 {es ? "Cargando visitantes..." : "Loading visitors..."}
    </div>
  );

  return (
    <div>


      {/* Subtitle + stats */}
      <p style={{ textAlign: "center", color: "#c0a0c8", fontSize: 12, margin: "0 0 10px", fontWeight: 600 }}>
        {es
          ? `${visitors.length} visitantes · ${residentCount} residentes ✓`
          : `${visitors.length} visitors · ${residentCount} residents ✓`}
      </p>
      <p style={{ textAlign: "center", color: "#c8b0d8", fontSize: 11, margin: "0 0 14px", lineHeight: 1.5 }}>
        {es
          ? "Apunta en qué cabaña y zona vive cada visitante para no perderlos 🏡"
          : "Keep track of which cabin and zone each visitor lives in 🏡"}
        <br/>
        <span style={{ fontSize: 10, color: "#d0c0e0" }}>
          {es ? "☁️ Los cambios se guardan automáticamente" : "☁️ Changes save automatically"}
        </span>
      </p>

      {/* Search + Add */}
      <div style={{ marginBottom: 14 }}>
        <input type="text" placeholder={es ? "🔍 Buscar visitante..." : "🔍 Search visitor..."}
          value={q} onChange={e => setQ(e.target.value)}
          style={{ width: "100%", padding: "10px 16px", borderRadius: 99, border: "2px solid #e8d0f0", fontSize: 13, fontFamily: "Nunito, sans-serif", background: "rgba(255,255,255,0.85)", outline: "none", color: "#7a5090", boxSizing: "border-box" }} />

      </div>

      {/* Visitor list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
        {filtered.map(v => (
          <VisitorCard
            key={v.name}
            name={v.name}
            data={progress[v.name] || {}}
            onChange={fields => handleChange(v.name, fields)}
            onDelete={() => handleDelete(v.name)}
            isCustom={v.custom}
            lang={lang}
          />
        ))}
        {filtered.length === 0 && (
          <p style={{ textAlign: "center", color: "#c8b0d8", fontSize: 13, padding: "30px 0" }}>
            {es ? "No se encontraron visitantes 🐾" : "No visitors found 🐾"}
          </p>
        )}
      </div>
    </div>
  );
}


// ─── Donation Popup ───────────────────────────────────────────────────────────

function DonationPopup({ lang = 'es', onClose }) {
  const es = lang === 'es';
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(60,20,90,0.55)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(4px)" }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 28, padding: "32px 28px", maxWidth: 380, width: "100%", boxShadow: "0 12px 50px rgba(120,40,200,0.25)", textAlign: "center", position: "relative", border: "2px solid #f0d8ff" }}>

        {/* Close button */}
        <button onClick={onClose} style={{ position: "absolute", top: 14, right: 16, background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#c0a0d0", lineHeight: 1 }}>✕</button>

        {/* Emoji header */}
        <div style={{ fontSize: 48, marginBottom: 6 }}>☕🌸</div>

        {/* Title */}
        <h2 style={{ margin: "0 0 12px", fontSize: 20, fontWeight: 900, color: "#5a2878", fontFamily: "Nunito, sans-serif" }}>
          {es ? "¡Hola, coleccionador/a! 💜" : "Hi, flower collector! 💜"}
        </h2>

        {/* Message */}
        <p style={{ fontSize: 13, color: "#7050a0", lineHeight: 1.75, margin: "0 0 10px", fontFamily: "Nunito, sans-serif" }}>
          {es
            ? <>Esta app la hago con mucho amor en mi tiempo libre para que podamos llevar nuestras flores al día. <b>Si te está siendo útil</b>, considera invitarme a un cafecito — me ayuda a pagar el hosting y a seguir mejorándola. 🌷</>
            : <>This app is made with lots of love in my free time so we can all keep track of our flowers. <b>If you're finding it useful</b>, consider buying me a coffee — it helps me cover hosting costs and keep improving it. 🌷</>}
        </p>
        <p style={{ fontSize: 12, color: "#c0a0c8", margin: "0 0 22px", fontFamily: "Nunito, sans-serif", fontStyle: "italic" }}>
          {es ? "Cualquier cantidad es un abrazo enorme 🤗" : "Any amount is a huge hug 🤗"}
        </p>

        {/* CTA button */}
        <a href="https://paypal.me/jessidani86" target="_blank" rel="noopener noreferrer" onClick={onClose}
          style={{ display: "block", background: "linear-gradient(135deg,#f0a030,#e07010)", color: "#fff", borderRadius: 14, padding: "13px 20px", textDecoration: "none", fontWeight: 900, fontSize: 15, fontFamily: "Nunito, sans-serif", boxShadow: "0 4px 16px rgba(220,130,20,0.35)", marginBottom: 12, transition: "opacity 0.2s" }}
          onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
          onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
          ☕ {es ? "¡Invitar a un café!" : "Buy me a coffee!"}
        </a>

        {/* Skip link */}
        <button onClick={onClose}
          style={{ background: "none", border: "none", color: "#c0a0c8", fontSize: 12, cursor: "pointer", fontFamily: "Nunito, sans-serif", fontWeight: 600, textDecoration: "underline" }}>
          {es ? "Ahora no, quizás luego 🌸" : "Not now, maybe later 🌸"}
        </button>

        {/* Footer */}
        <p style={{ fontSize: 10, color: "#e0c8f0", margin: "14px 0 0", fontFamily: "Nunito, sans-serif" }}>
          {es ? "Este mensaje aparece una vez a la semana" : "This message appears once a week"} ·{" "}
          <a href="https://www.instagram.com/microjessii" target="_blank" rel="noopener noreferrer" style={{ color: "#c8a0e0", fontWeight: 700, textDecoration: "none" }}>@microjessii</a>
        </p>
      </div>
    </div>
  );
}

// ─── Tutorial Tab ─────────────────────────────────────────────────────────────

function TutorialTab({ lang = 'es' }) {
  const [os, setOs] = useState(() => {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
    if (/Android/.test(ua)) return 'android';
    return 'desktop';
  });

  const S = {
    card: (bg, border) => ({ background: bg, borderRadius: 20, border: `1.5px solid ${border}`, padding: "18px 20px", boxShadow: "0 2px 12px rgba(180,130,220,0.09)" }),
    h: (color) => ({ margin: "0 0 12px", fontSize: 16, fontWeight: 900, color }),
    step: { display: "flex", gap: 12, alignItems: "flex-start", background: "rgba(255,255,255,0.65)", borderRadius: 12, padding: "10px 14px" },
    num: (bg, color) => ({ width: 26, height: 26, borderRadius: "50%", background: bg, color, fontWeight: 900, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }),
    txt: (color) => ({ fontSize: 12, color, lineHeight: 1.6 }),
    tip: (bg, border, color) => ({ background: bg, border: `1.5px solid ${border}`, borderRadius: 12, padding: "10px 14px", fontSize: 12, color, fontWeight: 600, marginTop: 10 }),
  };

  const es = lang === 'es';

  const welcome = es
    ? { title: "¡Bienvenido/a al Tracker de Flores! 🌸", body: 'Esta app te ayuda a llevar el control de todos los colores de flores que has conseguido en <b>Hello Kitty Island Adventure</b>. Puedes marcar los colores que tienes, añadir fotos de tus flores, y seguir tu progreso en todas las colecciones. ¡Todo se guarda automáticamente en la nube y puedes acceder desde cualquier dispositivo! ☁️' }
    : { title: "Welcome to the Flower Tracker! 🌸", body: 'This app helps you keep track of all the flower colors you\'ve collected in <b>Hello Kitty Island Adventure</b>. You can mark the colors you have, add photos of your flowers, and follow your progress across all collections. Everything saves automatically to the cloud and you can access it from any device! ☁️' };

  const howToUse = es
    ? { title: "🌿 Cómo usar el tracker", steps: [
        ["1", "Usa las pestañas de arriba para navegar entre <b>Regionales</b>, <b>Eventos</b>, <b>Colores</b> y la <b>Guía de cruces</b>."],
        ["2", "Dentro de cada flor verás sus <b>colores base</b> — los que puedes conseguir directamente con semillas o en su zona. Tócalos para marcarlos como obtenidos."],
        ["3", "Pulsa <b>\"Ver todos los colores\"</b> para desplegar los 29 colores posibles de esa flor y marcar los que vayas consiguiendo por cruce."],
        ["4", "La <b>barra de progreso</b> de cada flor y el contador global del header se actualizan solos. ¡Así sabes siempre cuánto te falta!"],
        ["5", "Usa el buscador para encontrar una flor rápidamente por su nombre en español o inglés."],
      ]}
    : { title: "🌿 How to use the tracker", steps: [
        ["1", "Use the tabs at the top to navigate between <b>Regional</b>, <b>Events</b>, <b>Colors</b>, and the <b>Breeding Guide</b>."],
        ["2", "Inside each flower you'll see its <b>base colors</b> — the ones you can get directly from seeds or in their zone. Tap them to mark them as obtained."],
        ["3", "Press <b>\"Show all colors\"</b> to expand all 29 possible colors for that flower and mark the ones you get through breeding."],
        ["4", "The <b>progress bar</b> for each flower and the global counter in the header update automatically. You'll always know how much is left!"],
        ["5", "Use the search bar to quickly find a flower by its Spanish or English name."],
      ]};


  const addFlowerSection = es
    ? { title: "🌱 Cómo añadir flores nuevas", intro: "Si el juego añade flores nuevas en una actualización, o si quieres añadir una que falta, puedes hacerlo tú misma sin esperar a que actualice la app:", steps: [
        ["1", "Asegúrate de estar en la pestaña <b>Regionales</b> o <b>Eventos</b>."],
        ["2", "Pulsa el botón morado <b>\"+ Nueva flor\"</b> que aparece arriba a la derecha."],
        ["3", "Elige si es una flor regional o de evento y rellena su nombre, patrón y origen."],
        ["4", "Selecciona sus colores base (los que se consiguen sin cruzar)."],
        ["5", "Pulsa <b>\"Añadir flor\"</b> — aparecerá al final de la lista con una etiqueta ✨ nueva."],
      ], tip: "🗑️ Para eliminar una flor que añadiste tú, pulsa el botón de papelera que aparece en la esquina de la tarjeta." }
    : { title: "🌱 How to add new flowers", intro: "If the game adds new flowers in an update, or if you want to add one that's missing, you can do it yourself without waiting for the app to update:", steps: [
        ["1", "Make sure you're on the <b>Regional</b> or <b>Events</b> tab."],
        ["2", "Press the purple <b>\"+ New flower\"</b> button at the top right."],
        ["3", "Choose whether it's a regional or event flower and fill in its name, pattern, and origin."],
        ["4", "Select its base colors (the ones you can get without breeding)."],
        ["5", "Press <b>\"Add flower\"</b> — it will appear at the bottom of the list with a ✨ new badge."],
      ], tip: "🗑️ To delete a flower you added, press the trash icon that appears in the corner of the card." };

  const mobileSection = es
    ? { title: "📱 Usar la app desde el móvil como si fuera una app", intro: "Puedes añadir esta página a la pantalla de inicio de tu móvil para abrirla como una app real, sin necesidad de abrir el navegador cada vez. ¡Incluso funciona sin conexión una vez cargada!",
        osBtns: [["ios","🍎 iPhone / iPad"],["android","🤖 Android"],["desktop","💻 Ordenador"]],
        ios: [["1","Abre <b>hkia.microjessi.com</b> en Safari (importante: tiene que ser Safari, no Chrome ni otro navegador)."],["2","Toca el botón de <b>compartir</b> — es el icono de la cajita con una flecha hacia arriba ↑, en la barra inferior."],["3","Desplázate por el menú y toca <b>\"Añadir a pantalla de inicio\"</b>."],["4","Ponle el nombre que quieras (por ejemplo \"HKIA Flores\") y toca <b>\"Añadir\"</b>."],["5","¡Listo! Aparecerá un icono en tu pantalla de inicio como cualquier app. 🌸"]],
        android: [["1","Abre <b>hkia.microjessi.com</b> en Chrome."],["2","Toca el menú de los <b>tres puntitos</b> ⋮ en la esquina superior derecha."],["3","Toca <b>\"Añadir a pantalla de inicio\"</b> o <b>\"Instalar app\"</b>."],["4","Confirma tocando <b>\"Añadir\"</b> en el mensaje que aparece."],["5","¡Aparecerá el icono en tu pantalla de inicio! 🌸"]],
        desktop: [["1","Abre <b>hkia.microjessi.com</b> en Chrome o Edge."],["2","Busca el icono de <b>instalar</b> (⊕) en la barra de direcciones, a la derecha."],["3","Haz clic en él y selecciona <b>\"Instalar\"</b>."],["4","La app se abrirá en su propia ventana y tendrás un acceso directo en el escritorio. 🌸"]],
        credit: "¡Si tienes dudas, escríbeme!" }
    : { title: "📱 Use the app on mobile like a real app", intro: "You can add this page to your phone's home screen to open it like a real app, without needing to open the browser every time. It even works offline once loaded!",
        osBtns: [["ios","🍎 iPhone / iPad"],["android","🤖 Android"],["desktop","💻 Computer"]],
        ios: [["1","Open <b>hkia.microjessi.com</b> in Safari (important: it must be Safari, not Chrome or another browser)."],["2","Tap the <b>Share</b> button — it's the box icon with an arrow pointing up ↑, in the bottom bar."],["3","Scroll through the menu and tap <b>\"Add to Home Screen\"</b>."],["4","Give it a name (e.g. \"HKIA Flowers\") and tap <b>\"Add\"</b>."],["5","Done! An icon will appear on your home screen just like any app. 🌸"]],
        android: [["1","Open <b>hkia.microjessi.com</b> in Chrome."],["2","Tap the <b>three-dot menu</b> ⋮ in the top right corner."],["3","Tap <b>\"Add to Home screen\"</b> or <b>\"Install app\"</b>."],["4","Confirm by tapping <b>\"Add\"</b> in the prompt that appears."],["5","The icon will appear on your home screen! 🌸"]],
        desktop: [["1","Open <b>hkia.microjessi.com</b> in Chrome or Edge."],["2","Look for the <b>install</b> icon (⊕) in the address bar on the right."],["3","Click it and select <b>\"Install\"</b>."],["4","The app will open in its own window and you'll have a shortcut on your desktop. 🌸"]],
        credit: "If you have any questions, feel free to message me!" };

  const m = mobileSection;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, paddingBottom: 10 }}>

      <div style={S.card("linear-gradient(135deg,#fdf0ff,#f0e8ff)", "#e0c8f8")}>
        <h2 style={{ ...S.h("#6030a0"), fontSize: 18 }}>{welcome.title}</h2>
        <p style={{ fontSize: 13, color: "#7050a0", lineHeight: 1.7, margin: 0 }} dangerouslySetInnerHTML={{ __html: welcome.body }} />
      </div>

      <div style={S.card("linear-gradient(135deg,#f0fff8,#e8fef0)", "#b8e8c8")}>
        <h3 style={S.h("#206040")}>{howToUse.title}</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {howToUse.steps.map(([n, txt]) => (
            <div key={n} style={S.step}>
              <div style={S.num("linear-gradient(135deg,#48b870,#208050)", "#fff")}>{n}</div>
              <span style={S.txt("#307050")} dangerouslySetInnerHTML={{ __html: txt }} />
            </div>
          ))}
        </div>
      </div>


      <div style={S.card("linear-gradient(135deg,#fdf0ff,#f8e8ff)", "#e0c0f8")}>
        <h3 style={S.h("#6020a0")}>{addFlowerSection.title}</h3>
        <p style={{ fontSize: 12, color: "#8040b0", lineHeight: 1.6, margin: "0 0 12px" }}>{addFlowerSection.intro}</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {addFlowerSection.steps.map(([n, txt]) => (
            <div key={n} style={S.step}>
              <div style={S.num("linear-gradient(135deg,#c060e0,#8030b0)", "#fff")}>{n}</div>
              <span style={S.txt("#6030a0")} dangerouslySetInnerHTML={{ __html: txt }} />
            </div>
          ))}
        </div>
        <div style={S.tip("#fdf0ff", "#e0c0f8", "#7030a0")}>{addFlowerSection.tip}</div>
      </div>

      <div style={S.card("linear-gradient(135deg,#fff0f8,#fde8f4)", "#f0c0e0")}>
        <h3 style={S.h("#801050")}>{m.title}</h3>
        <p style={{ fontSize: 12, color: "#a03060", lineHeight: 1.6, margin: "0 0 14px" }}>{m.intro}</p>
        <div style={{ marginBottom: 14 }}>
          {m.osBtns.map(([id, label]) => (
            <button key={id} onClick={() => setOs(id)}
              style={{ flex: 1, padding: "8px 4px", borderRadius: 12, border: `2px solid ${os === id ? "#d040a0" : "#f0c0e0"}`, background: os === id ? "linear-gradient(135deg,#f8c0e8,#f0a8d8)" : "#fff8fc", color: os === id ? "#801050" : "#c080a0", fontWeight: 800, fontSize: 11, cursor: "pointer", transition: "all 0.15s", fontFamily: "Nunito, sans-serif" }}>
              {label}
            </button>
          ))}
        </div>
        {os === 'ios' && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {m.ios.map(([n, txt]) => (
              <div key={n} style={S.step}>
                <div style={S.num("linear-gradient(135deg,#e060c0,#b03090)", "#fff")}>{n}</div>
                <span style={S.txt("#801050")} dangerouslySetInnerHTML={{ __html: txt }} />
              </div>
            ))}
          </div>
        )}
        {os === 'android' && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {m.android.map(([n, txt]) => (
              <div key={n} style={S.step}>
                <div style={S.num("linear-gradient(135deg,#60c060,#208040)", "#fff")}>{n}</div>
                <span style={S.txt("#206040")} dangerouslySetInnerHTML={{ __html: txt }} />
              </div>
            ))}
          </div>
        )}
        {os === 'desktop' && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {m.desktop.map(([n, txt]) => (
              <div key={n} style={S.step}>
                <div style={S.num("linear-gradient(135deg,#6090e0,#2050b0)", "#fff")}>{n}</div>
                <span style={S.txt("#204080")} dangerouslySetInnerHTML={{ __html: txt }} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ textAlign: "center", padding: "4px 0 8px" }}>
        <span style={{ fontSize: 11, color: "#d0a8e0", fontWeight: 600 }}>
          {es ? "Guías y contenido por" : "Guides and content by"}{" "}
          <a href="https://www.instagram.com/microjessii" target="_blank" rel="noopener noreferrer" style={{ color: "#c080d0", fontWeight: 800, textDecoration: "none" }}>@microjessii</a>
          {" "}· {m.credit} 💜
        </span>
      </div>
    </div>
  );
}

// ─── Guide Tab ─────────────────────────────────────────────────────────────────

// ─── PatternsTab ──────────────────────────────────────────────────────────────

function PatternsTab({ lang = 'es', userId }) {
  const es = lang === 'es';
  const SHEET_URL = "https://docs.google.com/spreadsheets/d/1-JUfqPdcikEqAGjtf7c_2AZNFekf3M55M_gSBmlAnnU/edit?usp=sharing";
  const STORAGE_KEY = "hkia_patterns_sheet_url";
  const SETTING_KEY = "patterns_sheet_url";

  const [savedUrl, setSavedUrl] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [inputUrl, setInputUrl] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Load from Supabase on mount
  useEffect(() => {
    if (!userId) return;
    supabase
      .from('user_settings')
      .select('value')
      .eq('user_id', userId)
      .eq('key', SETTING_KEY)
      .single()
      .then(({ data }) => {
        if (data?.value) {
          setSavedUrl(data.value);
          localStorage.setItem(STORAGE_KEY, data.value);
        }
      });
  }, [userId]);

  async function handleSave() {
    const trimmed = inputUrl.trim();
    if (!trimmed) return;
    setSaving(true);
    setLoadError(false);
    // Save to localStorage immediately
    localStorage.setItem(STORAGE_KEY, trimmed);
    setSavedUrl(trimmed);
    // Save to Supabase
    if (userId) {
      const { error } = await supabase
        .from('user_settings')
        .upsert({ user_id: userId, key: SETTING_KEY, value: trimmed, updated_at: new Date().toISOString() }, { onConflict: 'user_id,key' });
      if (error) setLoadError(true);
    }
    setSaving(false);
    setInputUrl("");
    setEditing(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  async function handleRemove() {
    localStorage.removeItem(STORAGE_KEY);
    setSavedUrl("");
    setEditing(false);
    if (userId) {
      await supabase
        .from('user_settings')
        .delete()
        .eq('user_id', userId)
        .eq('key', SETTING_KEY);
    }
  }

  const s = {
    container: { maxWidth: 600, margin: "0 auto", padding: "16px 12px" },
    card: { background: "#fff", borderRadius: 18, border: "1.5px solid #e0d0f8", padding: "20px 18px", marginBottom: 16, boxShadow: "0 2px 12px rgba(140,80,220,0.07)" },
    title: { fontWeight: 900, fontSize: 17, color: "#6030a0", marginBottom: 6 },
    body: { fontSize: 13, color: "#7050a0", lineHeight: 1.6, marginBottom: 14 },
    btn: (primary) => ({
      display: "inline-flex", alignItems: "center", gap: 7,
      padding: "10px 20px", borderRadius: 12, fontWeight: 800, fontSize: 13,
      cursor: "pointer", border: "none", textDecoration: "none",
      background: primary ? "linear-gradient(135deg,#a040d0,#7020a0)" : "#f3ecff",
      color: primary ? "#fff" : "#7030b0",
      boxShadow: primary ? "0 3px 10px rgba(140,60,200,0.3)" : "none",
    }),
    input: { width: "100%", padding: "10px 14px", borderRadius: 10, border: "1.5px solid #d8c8f0", fontSize: 13, color: "#5030a0", outline: "none", boxSizing: "border-box", fontFamily: "inherit" },
    step: { display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 12 },
    stepNum: { width: 26, height: 26, borderRadius: "50%", background: "linear-gradient(135deg,#c060e0,#8030b0)", color: "#fff", fontWeight: 900, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 },
    stepText: { fontSize: 13, color: "#604080", lineHeight: 1.55 },
    tag: { display: "inline-block", background: "#f0e8ff", color: "#8040c0", borderRadius: 6, padding: "1px 8px", fontSize: 11, fontWeight: 700, marginLeft: 4 },
    savedCard: { background: "linear-gradient(135deg,#f5eeff,#ede0ff)", borderRadius: 14, border: "1.5px solid #d0b0f0", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 },
    savedUrl: { fontSize: 11, color: "#9060c0", wordBreak: "break-all", background: "#fff", borderRadius: 8, padding: "6px 10px", border: "1px solid #e0d0f0" },
  };

  const steps_es = [
    { text: <>Haz clic en <strong>"Abrir plantilla"</strong> para ver el archivo en Google Sheets.</> },
    { text: <>En Google Sheets, ve a <strong>Archivo → Hacer una copia</strong>. Esto crea tu versión personal que puedes editar.</> },
    { text: <>Una vez tengas tu copia, copia la URL de la barra del navegador y pégala abajo para tener acceso directo siempre.</> },
  ];

  const steps_en = [
    { text: <>Click <strong>"Open template"</strong> to view the file in Google Sheets.</> },
    { text: <>In Google Sheets, go to <strong>File → Make a copy</strong>. This creates your personal version that you can edit.</> },
    { text: <>Once you have your copy, paste its URL below for quick access anytime.</> },
  ];

  const steps = es ? steps_es : steps_en;

  return (
    <div style={s.container}>

      {/* Intro card */}
      <div style={s.card}>
        <div style={s.title}>
          🧩 {es ? "Tracker de Patrones en Google Sheets" : "Pattern Tracker in Google Sheets"}
        </div>
        <div style={s.body}>
          {es
            ? "Lleva el control de todos los patrones de flores que has desbloqueado usando esta plantilla de Google Sheets. Incluye hojas para patrones regionales, de eventos, degradadas, rayas, anillo, moteado, ribeteado, confeti, manchas y más."
            : "Keep track of all the flower patterns you've unlocked using this Google Sheets template. Includes sheets for regional patterns, event patterns, ombre, stripes, ring, speckled, trim, confetti, patches and more."}
        </div>
        <a href={SHEET_URL} target="_blank" rel="noreferrer" style={s.btn(true)}>
          <span style={{ fontSize: 16 }}>📋</span>
          {es ? "Abrir plantilla" : "Open template"}
          <span style={{ fontSize: 12, opacity: 0.8 }}>↗</span>
        </a>
      </div>

      {/* Instructions card */}
      <div style={s.card}>
        <div style={{ fontWeight: 800, fontSize: 14, color: "#6030a0", marginBottom: 14 }}>
          {es ? "¿Cómo usarla?" : "How to use it?"}
        </div>
        {steps.map((step, i) => (
          <div key={i} style={s.step}>
            <div style={s.stepNum}>{i + 1}</div>
            <div style={s.stepText}>{step.text}</div>
          </div>
        ))}
        <div style={{ background: "#fffbec", border: "1px solid #f0e090", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "#806020", marginTop: 4 }}>
          💡 {es
            ? "Necesitas una cuenta de Google para poder hacer tu propia copia del archivo."
            : "You need a Google account to make your own copy of the file."}
        </div>
      </div>

      {/* My sheet card */}
      <div style={s.card}>
        <div style={{ fontWeight: 800, fontSize: 14, color: "#6030a0", marginBottom: 10 }}>
          🔗 {es ? "Mi copia personal" : "My personal copy"}
        </div>

        {savedUrl && !editing ? (
          <div style={s.savedCard}>
            <div style={{ fontSize: 13, color: "#7040a0", fontWeight: 700 }}>
              ✅ {es ? "¡Tu link está guardado!" : "Your link is saved!"}
            </div>
            <div style={s.savedUrl}>{savedUrl}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <a href={savedUrl} target="_blank" rel="noreferrer" style={s.btn(true)}>
                <span>📊</span> {es ? "Abrir mi tracker" : "Open my tracker"}
                <span style={{ fontSize: 12, opacity: 0.8 }}>↗</span>
              </a>
              <button onClick={() => { setEditing(true); setInputUrl(savedUrl); }} style={s.btn(false)}>
                ✏️ {es ? "Cambiar link" : "Change link"}
              </button>
              <button onClick={handleRemove} style={{ ...s.btn(false), color: "#c04040", background: "#fff0f0" }}>
                🗑️ {es ? "Eliminar" : "Remove"}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 13, color: "#9060b0", marginBottom: 10 }}>
              {es
                ? "Pega aquí la URL de tu copia de Google Sheets para tener acceso directo desde la app:"
                : "Paste the URL of your Google Sheets copy here for quick access from the app:"}
            </div>
            <input
              style={s.input}
              placeholder={es ? "https://docs.google.com/spreadsheets/d/..." : "https://docs.google.com/spreadsheets/d/..."}
              value={inputUrl}
              onChange={e => setInputUrl(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSave()}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              <button onClick={handleSave} style={{ ...s.btn(true), opacity: (!inputUrl.trim() || saving) ? 0.6 : 1 }} disabled={!inputUrl.trim() || saving}>
                {saving ? "⏳" : "💾"} {es ? (saving ? "Guardando..." : "Guardar link") : (saving ? "Saving..." : "Save link")}
              </button>
              {editing && (
                <button onClick={() => { setEditing(false); setInputUrl(""); }} style={s.btn(false)}>
                  {es ? "Cancelar" : "Cancel"}
                </button>
              )}
            </div>
            {saved && !loadError && (
              <div style={{ marginTop: 10, fontSize: 13, color: "#40a060", fontWeight: 700 }}>
                ✅ {es ? "¡Link guardado en el servidor!" : "Link saved to server!"}
              </div>
            )}
            {loadError && (
              <div style={{ marginTop: 10, fontSize: 13, color: "#c04040", fontWeight: 700 }}>
                ⚠️ {es ? "Guardado localmente, pero hubo un error al sincronizar con el servidor." : "Saved locally, but there was an error syncing to the server."}
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}

// ─── GuideTab ──────────────────────────────────────────────────────────────

function GuideTab({ lang = 'es' }) {
  const es = lang === 'es';

  const methods = es ? [
    { color: "#fff8f0", border: "#f8dcc8", tc: "#c06020", ic: "#a06040", icon: "🎨", title: "Mezclar colores",
      body: "Planta dos flores del mismo tipo pero distinto color tocando una parcela vacía. El retoño puede salir con un color mezclado. Por ejemplo, rojo + amarillo puede dar naranja.",
      example: "🌹 + 🌼 = 🌸 (mezcla de colores)",
      tip: "La probabilidad de conseguir el color exacto es baja — el resultado más común es un duplicado de uno de los padres. Usa fertilizante para aumentar las chances." },
    { color: "#f0fff8", border: "#c8f0d8", tc: "#207850", ic: "#408060", icon: "🔄", title: "Transferir colores",
      body: "Combina una flor con patrón (de un color concreto) con una flor de otro tipo (cualquier color). El color del patrón se transferirá al otro tipo de flor. Así puedes conseguir los 7 colores \"solo transferencia\": blanco, negro, rojo, amarillo, celeste, azul y rosa intenso.",
      example: "🌺(patrón rojo) + 🌸(cualquier color) = 🌸 roja",
      tip: "El color que se transfiere es el del primer cuadrito del inventario de la flor con patrón." },
    { color: "#f8f0ff", border: "#e0c8f8", tc: "#7030b0", ic: "#6040a0", icon: "✨", title: "Obtener patrones",
      body: "Fertiliza una flor para intentar que aparezca un patrón. La probabilidad es de ~1% por intento. Llevar a Cogimyun de acompañante sube las probabilidades a 5%.",
      example: "🌸 + 🧪 fertilizante = 🌸✨ (con patrón, con suerte)",
      tip: "Cada tipo de flor tiene patrones predeterminados distintos. Si la flor ya tiene patrón, el fertilizante solo aumenta el spawn de retoños." },
    { color: "#fff0f8", border: "#f8c8e0", tc: "#b02070", ic: "#a04070", icon: "🎴", title: "Transferir patrones",
      body: "Combina una flor con patrón y una flor de otro tipo del mismo color. Hay una probabilidad de 1–2% de que el retoño herede el patrón. ¡Lo más difícil de conseguir!",
      example: "🌺✨(roja, patrón) + 🌸(roja, sin patrón) = 🌸✨ (1–2%)",
      tip: "Los patrones híbridos (con dos colores) son los más raros y se obtienen cruzando flores con patrón entre sí." },
  ] : [
    { color: "#fff8f0", border: "#f8dcc8", tc: "#c06020", ic: "#a06040", icon: "🎨", title: "Color mixing",
      body: "Plant two flowers of the same type but different colors touching an empty plot. The sprout may come out with a mixed color. For example, red + yellow can produce orange.",
      example: "🌹 + 🌼 = 🌸 (color mix)",
      tip: "The chance of getting the exact color is low — the most common result is a duplicate of one of the parents. Use fertilizer to increase the odds." },
    { color: "#f0fff8", border: "#c8f0d8", tc: "#207850", ic: "#408060", icon: "🔄", title: "Color transfer",
      body: "Combine a patterned flower (of a specific color) with a flower of another type (any color). The pattern's color will transfer to the other flower type. This is how you get the 7 \"transfer only\" colors: white, black, red, yellow, sky, blue, and hot pink.",
      example: "🌺(red pattern) + 🌸(any color) = 🌸 red",
      tip: "The color that transfers is the one shown in the first square of the patterned flower's inventory icon." },
    { color: "#f8f0ff", border: "#e0c8f8", tc: "#7030b0", ic: "#6040a0", icon: "✨", title: "Getting patterns",
      body: "Fertilize a flower to try to get a pattern to appear. The chance is ~1% per attempt. Bringing Cinnamoroll as a companion raises it to 5%.",
      example: "🌸 + 🧪 fertilizer = 🌸✨ (patterned, if lucky)",
      tip: "Each flower type has its own set of default patterns. If a flower already has a pattern, fertilizer only increases the sprout spawn rate." },
    { color: "#fff0f8", border: "#f8c8e0", tc: "#b02070", ic: "#a04070", icon: "🎴", title: "Pattern transfer",
      body: "Combine a patterned flower with a flower of another type of the same color. There's a 1–2% chance the sprout will inherit the pattern. The hardest thing to get!",
      example: "🌺✨(red, patterned) + 🌸(red, no pattern) = 🌸✨ (1–2%)",
      tip: "Hybrid patterns (with two colors) are the rarest and are obtained by crossing two patterned flowers with each other." },
  ];

  const inventoryItems = es ? [
    ["🟪", "1er cuadrito = COLOR", "Es el color principal de la flor. Este es el color que se transferirá al cruzarla con otro tipo."],
    ["⬜", "2do cuadrito = PATRÓN", "Indica que la flor tiene un patrón especial (ribeteado, degradado…). Este patrón puede transferirse."],
  ] : [
    ["🟪", "1st square = COLOR", "This is the flower's main color. This is the color that will transfer when breeding it with another type."],
    ["⬜", "2nd square = PATTERN", "Indicates the flower has a special pattern (trim, ombre…). This pattern can be transferred."],
  ];

  const plantingPatterns = es ? [
    { nombre: "Fila clásica", emoji: "➖", desc: "El método más simple y efectivo. Perfecto para empezar.", patron: [["🟤","🟤","🟤","🟤","🟤","🟤","🟤"],["🟤","🌹","🌼","🟤","🌹","🌼","🟤"],["🟤","🟤","🟤","🟤","🟤","🟤","🟤"]], tip: "Simple y efectivo" },
    { nombre: "Fila extendida", emoji: "📏", desc: "Flores más separadas. Genera menos huecos de cruce.", patron: [["🟤","🟤","🟤","🟤","🟤","🟤","🟤","🟤"],["🟤","🌹","🟤","🌼","🟤","🌹","🟤","🌼"],["🟤","🟤","🟤","🟤","🟤","🟤","🟤","🟤"]], tip: "Buena para colores" },
    { nombre: "Modo ajedrez", emoji: "♟️", desc: "Alterna flores y huecos. Aprovecha al máximo el espacio.", patron: [["🌹","🟤","🌹","🟤","🌹"],["🟤","🌼","🟤","🌼","🟤"],["🌹","🟤","🌹","🟤","🌹"]], tip: "Máximos cruces posibles" },
    { nombre: "Patrón en bloque", emoji: "🎯", desc: "Flor central rodeada de huecos. Ideal para transferir patrones.", patron: [["🌼","🌼","🌼","🌼","🌹"],["🌼","🟤","🟤","🟤","🌹"],["🌼","🟤","🌺","🟤","🌹"],["🌼","🟤","🟤","🟤","🌹"],["🌼","🌼","🌼","🌹","🌹"]], tip: "Ideal para patrones raros" },
  ] : [
    { nombre: "Classic row", emoji: "➖", desc: "The simplest and most effective method. Perfect for beginners.", patron: [["🟤","🟤","🟤","🟤","🟤","🟤","🟤"],["🟤","🌹","🌼","🟤","🌹","🌼","🟤"],["🟤","🟤","🟤","🟤","🟤","🟤","🟤"]], tip: "Simple and effective" },
    { nombre: "Extended row", emoji: "📏", desc: "Flowers spaced further apart. Generates fewer breeding gaps.", patron: [["🟤","🟤","🟤","🟤","🟤","🟤","🟤","🟤"],["🟤","🌹","🟤","🌼","🟤","🌹","🟤","🌼"],["🟤","🟤","🟤","🟤","🟤","🟤","🟤","🟤"]], tip: "Good for color mixing" },
    { nombre: "Checkerboard", emoji: "♟️", desc: "Alternates flowers and gaps. Makes the most of available space.", patron: [["🌹","🟤","🌹","🟤","🌹"],["🟤","🌼","🟤","🌼","🟤"],["🌹","🟤","🌹","🟤","🌹"]], tip: "Maximum crosses possible" },
    { nombre: "Block pattern", emoji: "🎯", desc: "Central flower surrounded by gaps. Ideal for pattern transfer.", patron: [["🌼","🌼","🌼","🌼","🌹"],["🌼","🟤","🟤","🟤","🌹"],["🌼","🟤","🌺","🟤","🌹"],["🌼","🟤","🟤","🟤","🌹"],["🌼","🌼","🌼","🌹","🌹"]], tip: "Ideal for rare patterns" },
  ];

  const tips = es ? [
    ["🏡", "Empieza por completar los 29 colores de un solo tipo de flor antes de pasar al siguiente."],
    ["🧪", "Usa el Invernadero siempre que puedas — tasa de spawn del 100% sin necesidad de fertilizante."],
    ["💧", "Riega y fertiliza a diario para maximizar los retoños."],
    ["✂️", "Usa la palita para quitar duplicados no deseados y dejar huecos libres."],
    ["🐾", "Lleva a Cogimyun de acompañante para subir las probabilidades de patrón del 1% al 5%."],
    ["⏳", "Conseguir cruces puede llevar días o semanas. ¡Las mejores combinaciones llegan cuando menos lo esperas!"],
    ["🔄", "Si perdiste el control de la siembra, no tengas miedo de arrancar todo y empezar de cero."],
  ] : [
    ["🏡", "Start by completing all 29 colors of a single flower type before moving on to the next."],
    ["🧪", "Use the Greenhouse whenever you can — 100% sprout rate without needing fertilizer."],
    ["💧", "Water and fertilize daily to maximize sprouts."],
    ["✂️", "Use the shovel to remove unwanted duplicates and free up gaps."],
    ["🐾", "Bring Cinnamoroll as a companion to raise the pattern odds from 1% to 5%."],
    ["⏳", "Getting crosses can take days or weeks. The best combinations come when you least expect them!"],
    ["🔄", "If you've lost track of your planting, don't be afraid to dig everything up and start over."],
  ];

  const colorGroups = es ? [
    { label: "1. Colores base", desc: "De semillas o cápsulas", colors: ["blanco","negro","rojo","amarillo","celeste","azul","rosa_intenso"], badge: "Solo transferencia 🔄" },
    { label: "2. Mezclas directas", desc: "Cruza colores base entre sí", colors: ["naranja","verde","violeta","coral","lima","anil"], badge: "Mezcla 🎨" },
    { label: "3. + Blanco", desc: "Mezcla cualquier color con blanco", colors: ["rosa_calido","rosaceo","melocoton","crema","pistacho","menta","espuma_mar","nube","hielo","bigaro","lila","rosa_frio","rosa"], badge: "+ Blanco ⬜" },
    { label: "4. Combinaciones", desc: "Mezclas más elaboradas", colors: ["verde_azulado","magenta"], badge: "Combinar 🔀" },
  ] : [
    { label: "1. Base colors", desc: "From seeds or capsules", colors: ["blanco","negro","rojo","amarillo","celeste","azul","rosa_intenso"], badge: "Transfer only 🔄" },
    { label: "2. Direct mixes", desc: "Cross base colors with each other", colors: ["naranja","verde","violeta","coral","lima","anil"], badge: "Mix 🎨" },
    { label: "3. + White", desc: "Mix any color with white", colors: ["rosa_calido","rosaceo","melocoton","crema","pistacho","menta","espuma_mar","nube","hielo","bigaro","lila","rosa_frio","rosa"], badge: "+ White ⬜" },
    { label: "4. Combinations", desc: "More complex mixes", colors: ["verde_azulado","magenta"], badge: "Combine 🔀" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, paddingBottom: 10 }}>
      <div style={{ background: "linear-gradient(135deg,#fdf0ff,#f0e8ff)", borderRadius: 20, border: "1.5px solid #e0c8f8", padding: "20px 20px", boxShadow: "0 2px 12px rgba(180,130,220,0.1)" }}>
        <h2 style={{ margin: "0 0 10px", fontSize: 17, fontWeight: 900, color: "#6030a0" }}>
          {es ? "🌸 ¿Cómo funciona el cruce de flores?" : "🌸 How does flower breeding work?"}
        </h2>
        <p style={{ fontSize: 13, color: "#7050a0", lineHeight: 1.6, margin: 0 }}>
          {es
            ? 'Las flores en HKIA pueden cruzarse para obtener nuevos colores y patrones. Para cruzar dos flores, ambas deben estar tocando la misma <b>parcela vacía</b> — ahí nacerá el retoño nuevo. Las flores "padre" nunca cambian; solo generan nuevas flores en los huecos adyacentes.'
            : 'Flowers in HKIA can be bred to get new colors and patterns. To breed two flowers, both must be touching the same <b>empty plot</b> — that\'s where the new sprout will appear. Parent flowers never change; they only generate new flowers in adjacent gaps.'}
        </p>
        <div style={{ marginTop: 10, background: "rgba(255,255,255,0.6)", borderRadius: 12, padding: "10px 14px", fontSize: 12, color: "#9060c0", fontWeight: 600 }}>
          {es
            ? "💡 <b>Consejo:</b> Usa el <b>Invernadero</b> siempre que puedas — las flores allí tienen una tasa de retoño del 100%, lo que hace el cruce mucho más rápido."
            : "💡 <b>Tip:</b> Use the <b>Greenhouse</b> whenever you can — flowers there have a 100% sprout rate, making breeding much faster."}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 14 }}>
        {methods.map(m => (
          <div key={m.title} style={{ background: `linear-gradient(135deg,${m.color},${m.color})`, borderRadius: 18, border: `1.5px solid ${m.border}`, padding: "16px 18px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 24 }}>{m.icon}</span>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 900, color: m.tc }}>{m.title}</h3>
            </div>
            <p style={{ fontSize: 12, color: m.ic, lineHeight: 1.6, margin: "0 0 10px" }}>{m.body}</p>
            <div style={{ background: "rgba(255,255,255,0.7)", borderRadius: 10, padding: "8px 12px", fontSize: 12, color: m.tc, fontWeight: 600 }}>{m.example}</div>
            <div style={{ marginTop: 8, fontSize: 11, color: m.ic, lineHeight: 1.5 }}>{m.tip}</div>
          </div>
        ))}
      </div>

      <div style={{ background: "linear-gradient(135deg,#fffff0,#fffde8)", borderRadius: 20, border: "1.5px solid #f0e8a0", padding: "18px 20px" }}>
        <h3 style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 900, color: "#806000" }}>
          {es ? "🔍 Cómo leer una flor en el inventario" : "🔍 How to read a flower in your inventory"}
        </h3>
        <p style={{ fontSize: 12, color: "#806020", lineHeight: 1.6, margin: "0 0 10px" }}>
          {es ? "Cada flor con patrón muestra " : "Each patterned flower shows "}<b>{es ? "dos cuadritos" : "two squares"}</b>{es ? " en el inventario:" : " in the inventory:"}
        </p>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {inventoryItems.map(([ico, title, desc]) => (
            <div key={title} style={{ background: "rgba(255,255,255,0.8)", borderRadius: 12, padding: "10px 14px", flex: 1, minWidth: 120 }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>{ico}</div>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#604000" }}>{title}</div>
              <div style={{ fontSize: 11, color: "#806030", lineHeight: 1.4, marginTop: 2 }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: "linear-gradient(135deg,#f0f8ff,#e8f4ff)", borderRadius: 20, border: "1.5px solid #c0d8f8", padding: "18px 20px" }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 900, color: "#204080" }}>
          {es ? "🌱 Patrones de plantación" : "🌱 Planting patterns"}
        </h3>
        <p style={{ fontSize: 12, color: "#406080", lineHeight: 1.6, margin: "0 0 14px" }}>
          {es ? "La disposición de tus flores afecta cuántos cruces pueden ocurrir:" : "The arrangement of your flowers affects how many crosses can happen:"}
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 12 }}>
          {plantingPatterns.map(m => (
            <div key={m.nombre} style={{ background: "rgba(255,255,255,0.75)", borderRadius: 14, padding: "12px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <span style={{ fontSize: 18 }}>{m.emoji}</span>
                <span style={{ fontWeight: 800, fontSize: 13, color: "#305090" }}>{m.nombre}</span>
              </div>
              <p style={{ fontSize: 11, color: "#5070a0", lineHeight: 1.5, margin: "0 0 8px" }}>{m.desc}</p>
              <div style={{ background: "#f0f4ff", borderRadius: 8, padding: "6px 8px", marginBottom: 6 }}>
                {m.patron.map((row, i) => (
                  <div key={i} style={{ display: "flex", gap: 2, justifyContent: "center", marginBottom: 1 }}>
                    {row.map((cell, j) => <span key={j} style={{ fontSize: 13, width: 20, textAlign: "center" }}>{cell}</span>)}
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#4060a0", background: "#e8f0ff", borderRadius: 8, padding: "3px 8px", textAlign: "center" }}>✓ {m.tip}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: "linear-gradient(135deg,#f0fff0,#e8fee8)", borderRadius: 20, border: "1.5px solid #b8e8b8", padding: "18px 20px" }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 900, color: "#206020" }}>
          {es ? "💚 Consejos prácticos" : "💚 Practical tips"}
        </h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {tips.map(([ico, txt], i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "rgba(255,255,255,0.65)", borderRadius: 10, padding: "8px 12px" }}>
              <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{ico}</span>
              <span style={{ fontSize: 12, color: "#407040", lineHeight: 1.55 }}>{txt}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: "linear-gradient(135deg,#fff8fe,#fdf0fc)", borderRadius: 20, border: "1.5px solid #f0c8ec", padding: "18px 20px" }}>
        <h3 style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 900, color: "#801060" }}>
          {es ? "🗺️ Orden recomendado para conseguir los 29 colores" : "🗺️ Recommended order to get all 29 colors"}
        </h3>
        <p style={{ fontSize: 12, color: "#a03070", lineHeight: 1.6, margin: "0 0 12px" }}>
          {es
            ? '7 colores son "solo transferencia" — no se pueden mezclar, solo transferir desde una flor con patrón:'
            : '7 colors are "transfer only" — they can\'t be mixed, only transferred from a patterned flower:'}
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {colorGroups.map(g => (
            <div key={g.label} style={{ flex: 1, minWidth: 200, background: "rgba(255,255,255,0.7)", borderRadius: 12, padding: "10px 12px" }}>
              <div style={{ fontWeight: 800, fontSize: 12, color: "#801060", marginBottom: 2 }}>{g.label}</div>
              <div style={{ fontSize: 10, color: "#b04080", marginBottom: 6 }}>{g.desc}</div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>
                {g.colors.map(cid => { const c = cm[cid]; if (!c) return null; const dark = isDark(c.hex); return <span key={cid} style={{ fontSize: 9, background: c.hex, color: dark ? "#fff" : "#333", borderRadius: 99, padding: "2px 7px", fontWeight: 700, border: "1px solid rgba(0,0,0,0.1)" }}>{c[lang] || c.es}</span>; })}
              </div>
              <div style={{ fontSize: 9, background: "#f8e8f4", color: "#a03070", borderRadius: 8, padding: "2px 8px", fontWeight: 700, display: "inline-block" }}>{g.badge}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────

export default function App({ user, onLogout }) {
  const [regionales, setRegionales] = useState(DEFAULT_REGIONALES);
  const [eventos, setEventos] = useState(DEFAULT_EVENTOS);
  const [prog, setProg] = useState({});
  const [photos, setPhotos] = useState({});
  const [tab, setTab] = useState("reg");
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState(detectLang);
  const [showPopup, setShowPopup] = useState(() => {
    const last = localStorage.getItem('hkia-popup-seen');
    if (!last) return true;
    const diff = Date.now() - parseInt(last);
    return diff > 7 * 24 * 60 * 60 * 1000; // 1 week
  });

  const closePopup = () => {
    localStorage.setItem('hkia-popup-seen', Date.now().toString());
    setShowPopup(false);
  };

  const toggleLang = () => {
    const next = lang === 'es' ? 'en' : 'es';
    setLang(next);
    localStorage.setItem('hkia-lang', next);
  };

  const t = T[lang];

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [p, ph, custom] = await Promise.all([
        loadProgress(user.id),
        loadPhotos(user.id),
        loadCustomFlowers(user.id),
      ]);
      setProg(p);
      setPhotos(ph);
      const customReg = custom.filter(f => f.tipo === 'regional');
      const customEvt = custom.filter(f => f.tipo === 'evento');
      if (customReg.length) setRegionales(r => [...DEFAULT_REGIONALES, ...customReg]);
      if (customEvt.length) setEventos(e => [...DEFAULT_EVENTOS, ...customEvt]);
      setLoading(false);
    })();
  }, [user.id]);

  async function tog(fid, cid) {
    const checked = !!(prog[fid] || {})[cid];
    setProg(p => ({ ...p, [fid]: { ...(p[fid] || {}), [cid]: !checked } }));
    await toggleProgressDB(user.id, fid, cid, checked);
  }

  async function setPhoto(fid, data) {
    setPhotos(p => ({ ...p, [fid]: data }));
    await savePhotoDb(user.id, fid, data);
  }

  async function addFlor(form) {
    const id = "custom_" + Date.now();
    const flor = { ...form, id, custom: true };
    const saved = await addCustomFlowerDb(user.id, flor);
    const florWithSbId = { ...flor, supabase_id: saved.id };
    if (form.tipo === "regional") setRegionales(r => [...r, florWithSbId]);
    else setEventos(e => [...e, florWithSbId]);
    setShowAdd(false);
    setTab(form.tipo === "regional" ? "reg" : "evt");
  }

  async function deleteFlor(f) {
    if (f.supabase_id) await deleteCustomFlowerDb(f.supabase_id);
    if (f.tipo === "regional") setRegionales(r => r.filter(x => x.id !== f.id));
    else setEventos(e => e.filter(x => x.id !== f.id));
    setProg(p => { const n = { ...p }; delete n[f.id]; return n; });
    setPhotos(p => { const n = { ...p }; delete n[f.id]; return n; });
  }

  const all = [...regionales, ...eventos];
  const totC = all.length * COLORS.length;
  const totD = all.reduce((a, f) => a + COLORS.filter(c => (prog[f.id] || {})[c.id]).length, 0);
  const filt = list => list.filter(f => !q || f.es.toLowerCase().includes(q.toLowerCase()) || (f.en || '').toLowerCase().includes(q.toLowerCase()) || (f.evento || "").toLowerCase().includes(q.toLowerCase()));

  const TB = (id, label, act) => (
    <button onClick={() => setTab(id)}
      style={{ padding: "8px 18px", borderRadius: 99, border: "none", background: act ? "linear-gradient(135deg,#e070c8,#8858d0)" : "rgba(255,255,255,0.75)", color: act ? "#fff" : "#c090c0", fontWeight: 800, fontSize: 13, cursor: "pointer", boxShadow: act ? "0 2px 10px #d090d055" : "none", transition: "all 0.2s", fontFamily: "Nunito, sans-serif" }}>
      {label}
    </button>
  );

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(160deg,#fdf0ff,#f0f4ff 50%,#fff0f8)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Nunito, sans-serif" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🌸</div>
        <div style={{ fontSize: 14, color: "#c0a0c8", fontWeight: 700 }}>{t.loading}</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(160deg,#fdf0ff,#f0f4ff 50%,#fff0f8)", fontFamily: "Nunito, sans-serif", paddingBottom: 80 }}>
      {showAdd && <AddFlowerModal onAdd={addFlor} onClose={() => setShowAdd(false)} lang={lang} />}
      {showPopup && <DonationPopup lang={lang} onClose={closePopup} />}

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,#e070c8,#8858d0 50%,#5888e8)", padding: "28px 20px 22px", textAlign: "center", boxShadow: "0 4px 20px rgba(160,80,220,0.3)" }}>
        <div style={{ fontSize: 32, marginBottom: 4 }}>🌸🌺🌼💐🌷</div>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: "#fff", textShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>Hello Kitty Island Adventure</h1>
        <p style={{ margin: "4px 0 4px", color: "rgba(255,255,255,0.88)", fontSize: 13, fontWeight: 700 }}>{t.appSubtitle}</p>
        <p style={{ margin: "0 0 14px", color: "rgba(255,255,255,0.65)", fontSize: 11 }}>{user.email}</p>
        <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.2)", borderRadius: 16, padding: "12px 24px", minWidth: 220 }}>
          <span style={{ color: "rgba(255,255,255,0.9)", fontWeight: 700, fontSize: 12 }}>{t.totalProgress}</span>
          <div style={{ width: "100%", height: 10, borderRadius: 99, background: "rgba(255,255,255,0.25)", overflow: "hidden" }}>
            <div style={{ width: `${totC ? Math.round(totD / totC * 100) : 0}%`, height: "100%", background: "#fff", borderRadius: 99, transition: "width 0.4s" }} />
          </div>
          <span style={{ color: "#fff", fontWeight: 900, fontSize: 14 }}>{totD} / {totC} <span style={{ fontWeight: 600, fontSize: 11, opacity: 0.85 }}>{t.colors}</span></span>
        </div>
        <div style={{ marginTop: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
          <a href="https://paypal.me/jessidani86" target="_blank" rel="noopener noreferrer"
            style={{ background: "linear-gradient(135deg,rgba(255,200,80,0.35),rgba(255,160,40,0.35))", border: "1.5px solid rgba(255,220,100,0.6)", borderRadius: 99, padding: "5px 14px", color: "#fff", fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: "Nunito, sans-serif", display: "flex", alignItems: "center", gap: 5, textDecoration: "none" }}>
            ☕ {lang === 'es' ? 'Invítame a un café' : 'Buy me a coffee'}
          </a>
          <button onClick={toggleLang}
            style={{ background: "rgba(255,255,255,0.2)", border: "1.5px solid rgba(255,255,255,0.4)", borderRadius: 99, padding: "5px 14px", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "Nunito, sans-serif", display: "flex", alignItems: "center", gap: 5 }}>
            🌐 {lang === 'es' ? 'English' : 'Español'}
          </button>
          <button onClick={onLogout} style={{ background: "rgba(255,255,255,0.2)", border: "1.5px solid rgba(255,255,255,0.4)", borderRadius: 99, padding: "5px 16px", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "Nunito, sans-serif" }}>
            {t.logout}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", justifyContent: "center", gap: 8, padding: "18px 16px 10px", flexWrap: "wrap" }}>
        {TB("reg", t.tabReg, tab === "reg")}
        {TB("evt", t.tabEvt, tab === "evt")}
        {TB("patterns", t.tabPatterns, tab === "patterns")}
        {TB("col", t.tabCol, tab === "col")}
        {TB("guia", t.tabGuia, tab === "guia")}
        {TB("visitors", t.tabVisitors, tab === "visitors")}
        {TB("tutorial", t.tabTutorial, tab === "tutorial")}
      </div>

      {tab !== "col" && tab !== "guia" && tab !== "tutorial" && tab !== "visitors" && tab !== "patterns" && (
        <div style={{ padding: "0 16px 12px", maxWidth: 640, margin: "0 auto", display: "flex", gap: 8 }}>
          <input type="text" placeholder={t.searchPlaceholder} value={q} onChange={e => setQ(e.target.value)}
            style={{ width: "100%", padding: "10px 16px", borderRadius: 99, border: "2px solid #e8d0f0", fontSize: 13, fontFamily: "Nunito, sans-serif", background: "rgba(255,255,255,0.85)", outline: "none", color: "#7a5090", boxSizing: "border-box" }} />
          <button onClick={() => setShowAdd(true)}
            style={{ padding: "10px 20px", borderRadius: 99, border: "none", background: "linear-gradient(135deg,#e070c8,#8858d0)", color: "#fff", fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "Nunito, sans-serif", boxShadow: "0 2px 10px #d090d055", whiteSpace: "nowrap" }}>
            {t.addFlower}
          </button>
        </div>
      )}

      <div style={{ maxWidth: 680, margin: "0 auto", padding: "0 14px" }}>
        {tab === "reg" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            <p style={{ textAlign: "center", color: "#c0a0c8", fontSize: 12, margin: "0 0 4px", fontWeight: 600 }}>{t.regSubtitle(filt(regionales).length)}</p>
            {filt(regionales).map(f => <Card key={f.id} f={f} prog={prog[f.id] || {}} onToggle={cid => tog(f.id, cid)} onDelete={() => deleteFlor(f)} lang={lang} />)}
          </div>
        )}
        {tab === "evt" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            <p style={{ textAlign: "center", color: "#c0a0c8", fontSize: 12, margin: "0 0 4px", fontWeight: 600 }}>{t.evtSubtitle(filt(eventos).length)}</p>
            {filt(eventos).map(f => <Card key={f.id} f={f} prog={prog[f.id] || {}} onToggle={cid => tog(f.id, cid)} onDelete={() => deleteFlor(f)} isEvent lang={lang} />)}
          </div>
        )}
        {tab === "col" && (
          <div>
            <p style={{ textAlign: "center", color: "#c0a0c8", fontSize: 12, margin: "0 0 16px", fontWeight: 600 }}>{t.colSubtitle}</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))", gap: 10 }}>
              {COLORS.map(c => { const dark = isDark(c.hex); return (
                <div key={c.id} style={{ background: c.hex, borderRadius: 14, padding: "12px 10px", border: "2px solid rgba(0,0,0,0.06)", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: dark ? "#fff" : "#3a2850" }}>{c[lang] || c.es}</div>
                  <div style={{ fontSize: 10, color: dark ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.5)", fontStyle: "italic" }}>{lang === 'en' ? c.es : c.en}</div>
                  <div style={{ marginTop: 6, fontSize: 10, color: dark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.65)", background: dark ? "rgba(0,0,0,0.18)" : "rgba(255,255,255,0.65)", borderRadius: 8, padding: "3px 7px", fontWeight: 600 }}>{COLOR_MIX[c.id] || t.onlyTransfer}</div>
                </div>
              ); })}
            </div>
          </div>
        )}
        {tab === "guia" && <GuideTab lang={lang} />}
        {tab === "tutorial" && <TutorialTab lang={lang} />}
        {tab === "visitors" && <VisitorsTab userId={user.id} lang={lang} />}
        {tab === "patterns" && <PatternsTab lang={lang} userId={user.id} />}

        <div style={{ marginTop: 22, textAlign: "center" }}>
          <div style={{ fontSize: 11, color: "#c8a8d0", fontWeight: 600, padding: "10px 18px", background: "rgba(255,255,255,0.6)", borderRadius: 14, border: "1.5px dashed #e8c8f0", display: "inline-block" }}>
            {t.autoSave}
          </div>
        </div>
        <div style={{ marginTop: 14, textAlign: "center" }}>
          <div style={{ fontSize: 11, color: "#b090c8", fontWeight: 600, padding: "10px 18px", background: "rgba(255,255,255,0.6)", borderRadius: 14, border: "1.5px dashed #e0c0f0", display: "inline-block", lineHeight: 1.7 }}>
            {lang === 'es'
              ? <>¿Encontraste un error, tienes una sugerencia o alguna duda? 💌<br/>Escríbenos a <a href="mailto:hola@microjessi.com" style={{ color: "#a060c8", fontWeight: 800, textDecoration: "none" }}>hola@microjessi.com</a></>
              : <>Found a bug, have a suggestion or a question? 💌<br/>Write to us at <a href="mailto:hola@microjessi.com" style={{ color: "#a060c8", fontWeight: 800, textDecoration: "none" }}>hola@microjessi.com</a></>}
          </div>
        </div>
        <div style={{ marginTop: 12, textAlign: "center", paddingBottom: 8 }}>
          <span style={{ fontSize: 10, color: "#d0b8e0", fontWeight: 600 }}>{t.guideCredit} <a href="https://www.instagram.com/microjessii" target="_blank" rel="noopener noreferrer" style={{ color: "#d0b8e0", textDecoration: "none", fontWeight: 800 }}>@microjessii</a> · {t.trackerCredit} <b>Claude</b> ✨</span>
        </div>
      </div>
    </div>
  );
}
